import React, { useState, useRef, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { roomTypeTranslations, styleTranslations, colorThemeTranslations } from '../constants/translations';
import GalleryThumbnails from './GalleryThumbnails';
import { Alert } from '@mui/material';
import { useAuth, AuthProvider } from '../contexts/AuthContext';
import WorkflowTracker, { stages } from './WorkflowTracker';
import { Virtuoso } from 'react-virtuoso';
import { ErrorBoundary } from 'react-error-boundary';
import CreditAlert from './CreditAlert';
import { throttle } from 'lodash';
import { useSearchParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { styled, keyframes } from '@mui/system';
import { useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion'; // Import framer-motion
import { TutorialTip, FeatureTooltip } from './Onboarding';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  useTheme, 
  IconButton,
  Modal,
  Fade, 
  Grow, 
  CircularProgress,
  LinearProgress,
  Card,
  CardContent
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import TuneIcon from '@mui/icons-material/Tune';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HomeIcon from '@mui/icons-material/Home';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StarIcon from '@mui/icons-material/Star';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PaletteIcon from '@mui/icons-material/Palette';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ApartmentIcon from '@mui/icons-material/Apartment';
import RocketIcon from '@mui/icons-material/Rocket';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import GrassIcon from '@mui/icons-material/Grass';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'; // Import dropdown icon
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // Import back icon
import Menu from '@mui/material/Menu'; // Import Menu
import MenuItem from '@mui/material/MenuItem'; // Import MenuItem
import Popover from '@mui/material/Popover'; // Import Popover for color picker
import ChairIcon from '@mui/icons-material/Chair'; // Icon for furnished room toggle

import { supabase, consumeCredits, getPublicUrl, updateProfile } from '../supabaseClient';
import { canvasWorkflows } from '../workflows/canvasWorkflows';
import { runComfyDeployWorkflow } from '../api/comfyDeployApi';
// Import both Gemini generation and editing services
import { runGeminiImageGeneration, runGeminiMaskFreeEdit } from '../api/geminiApi'; 
import { enhancePrompt } from '../services/promptEnhancementService';
import { translationService } from '../services/translationEnhancementService';
import CustomTooltip from './CustomTooltip';
import MaskingCanvas from './MaskingCanvas';
import MaskSaveSuccess from './MaskSaveSuccess';
import convert from 'heic-convert/browser'; // Import heic-convert for browser

const CanvasOutputDisplay = lazy(() => import('./CanvasOutputDisplay'));
const ImageCompare = lazy(() => import('./ImageCompare'));

// Helper function to create and upload a thumbnail
const createAndUploadThumbnail = async (imageUrl, maxDimension = 300) => {
  if (!imageUrl) return null;

  try {
    // 1. Fetch the image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();

    // 2. Create an Image element
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = (err) => reject(err);
      image.src = URL.createObjectURL(blob);
    });
    URL.revokeObjectURL(img.src); // Clean up object URL

    // 3. Calculate new dimensions
    const { naturalWidth: width, naturalHeight: height } = img;
    let newWidth, newHeight;
    if (width > height) {
      newWidth = Math.min(width, maxDimension);
      newHeight = Math.round((height * newWidth) / width);
    } else {
      newHeight = Math.min(height, maxDimension);
      newWidth = Math.round((width * newHeight) / height);
    }

    // 4. Resize using Canvas
    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    // 5. Convert to WebP blob
    const webpBlob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/webp', 0.85); // Adjust quality (0.85 is a good balance)
    });

    if (!webpBlob) {
      throw new Error('Failed to create WebP blob from canvas');
    }

    // 6. Upload to Supabase
    const fileName = `thumbnails/${Date.now()}-thumb.webp`;
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('generated-images') // Assuming thumbnails go in the same bucket, under 'thumbnails/' prefix
      .upload(fileName, webpBlob, {
        contentType: 'image/webp',
        cacheControl: '3600', // Cache for 1 hour
        upsert: false,
      });

    if (uploadError) {
      throw uploadError;
    }

    // 7. Get Public URL
    const thumbnailUrl = getPublicUrl('generated-images', fileName);
    console.log("Thumbnail created and uploaded:", thumbnailUrl);
    return thumbnailUrl;

  } catch (error) {
    console.error("Error creating or uploading thumbnail:", error);
    return null; // Return null if thumbnail creation fails
  }
};

const MAX_UPLOAD_SIZE_MB = 5;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

const WorkflowCard = styled(Card)(({ theme, selected }) => ({
  border: 'none',
  borderRadius: '16px',
  transform: 'translateZ(0)',
  willChange: 'transform, background-color',
  backgroundColor: theme.palette.mode === 'dark'
    ? selected ? 'rgba(0, 74, 173, 0.15)' : 'rgba(26, 26, 38, 0.8)'
    : selected ? 'rgba(0, 74, 173, 0.05)' : '#fdfdfd',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  position: 'relative',
  overflow: 'visible',
  boxShadow: selected 
    ? '0 2px 8px rgba(0, 74, 173, 0.2)' 
    : theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.2)'
      : '0 1px 4px rgba(0,0,0,0.06)',
  backdropFilter: 'blur(8px)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-1px',
    borderRadius: '17px',
    padding: '1px',
    background: theme.palette.mode === 'dark'
      ? selected 
        ? 'linear-gradient(135deg, rgba(0, 74, 173, 0.5), rgba(162, 195, 164, 0.3))' 
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))'
      : selected 
        ? 'linear-gradient(135deg, #004aad, #A2C3A4, rgba(0, 74, 173, 0.5))' 
        : 'linear-gradient(135deg, rgba(255,255,255,0.7), rgba(255,255,255,0.3))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: selected ? 1 : 0.5,
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: selected ? 
      '0 4px 12px rgba(0, 74, 173, 0.2)' : 
      '0 4px 12px rgba(0,0,0,0.1)',
    borderColor: '#004aad',
  }
}));

const SelectButton = styled(Button)(({ theme, selected }) => ({
  background: selected 
    ? '#004aad' 
    : theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))'
      : 'rgba(0,0,0,0.1)',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  color: selected 
    ? '#fdfdfd' 
    : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.9)'
      : 'text.primary',
  backdropFilter: 'blur(8px)',
  padding: '8px 16px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  border: `1px solid ${selected 
    ? '#004aad' 
    : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.08)'}`,
  '&:hover': {
    background: selected 
      ? '#003580' 
      : theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.08))'
        : 'rgba(0,0,0,0.2)',
    borderColor: selected 
      ? '#003580' 
      : 'rgba(0, 74, 173, 0.3)',
  }
}));

// Revert GenerateButton to use styled(Button) directly, but accept progress prop
const GenerateButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'loading' && prop !== 'progress', // Add progress back
})(({ theme, loading, progress }) => ({ // Add progress back to props
  backgroundColor: '#004aad',
  color: '#fdfdfd',
  padding: '12px 32px',
  borderRadius: '8px',
  textTransform: 'none',
  width: '600px',
  margin: '0 auto',
  marginBottom: '32px',
  fontWeight: 600,
  fontSize: '1.1rem', // Slightly reduced font size
  position: 'relative', // Needed for pseudo-element positioning
  overflow: 'hidden', // Hide overflowing pseudo-element
  boxShadow: '0 4px 14px rgba(0, 74, 173, 0.3)',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), background-color 0.3s ease', // Added background-color transition
  zIndex: 1, // Ensure button is above other elements if needed
  '&:hover': {
    backgroundColor: loading ? '#004aad' : '#003580', // Prevent hover color change during loading
    boxShadow: '0 6px 20px rgba(0, 74, 173, 0.4)',
    transform: loading ? 'none' : 'translateY(-2px)', // Prevent hover transform during loading
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground, // Use theme standard disabled color
    color: theme.palette.action.disabled,
    boxShadow: 'none',
    cursor: 'not-allowed',
    '&::before': {
      width: '0 !important', // Ensure progress bar is hidden when disabled
    },
  },
  // Remove ::before pseudo-element styles for CSS animation

  // Ensure text is above the framer-motion animation layer
  '& .MuiButton-label, & .MuiButton-startIcon, & .MuiButton-endIcon, & > span': {
    position: 'relative', // Keep text relative
    zIndex: 2, // Increase zIndex to ensure text is definitely above the ::before element
  },
}));

const UploadArea = styled(Box)(({ theme, isDragging }) => ({
  border: `1px solid ${isDragging ? '#004aad' : 'rgba(0, 74, 173, 0.15)'}`,
  borderRadius: '16px',
  padding: { xs: '24px', sm: '36px', md: '48px' },
  textAlign: 'center',
  backgroundColor: isDragging 
    ? theme.palette.mode === 'dark' 
      ? 'rgba(26, 26, 38, 0.9)' 
      : 'rgba(0, 74, 173, 0.05)' 
    : theme.palette.mode === 'dark' 
      ? 'rgba(26, 26, 38, 0.95)' 
      : '#fdfdfd',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  position: 'relative',
  minHeight: '240px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(26, 26, 38, 0.8)'
      : 'rgba(203, 241, 153, 0.05)',
    borderColor: 'rgba(0, 74, 173, 0.3)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  },
  '& .MuiTypography-root': {
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#869d96',
    fontSize: '0.875rem',
    fontWeight: 500,
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  },
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 38, 0.95)' : '#fdfdfd',
  borderRadius: 20,
  padding: { xs: theme.spacing(2), sm: theme.spacing(4), md: theme.spacing(6) },
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 24px rgba(0, 0, 0, 0.3)' 
    : '0 4px 24px rgba(0, 0, 0, 0.06)',
  width: '100%',
  '& > *:not(:last-child)': {
    marginBottom: theme.spacing(4),
  },
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  '&:hover': {
    boxShadow: '0 6px 32px rgba(0, 0, 0, 0.15)',
  },
  marginBottom: theme.spacing(4),
  position: 'relative',
  backdropFilter: 'blur(8px)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: theme.palette.mode === 'dark' ? `
      radial-gradient(circle at center, rgba(0, 74, 173, 0.15) 1px, transparent 1px),
      radial-gradient(circle at center, rgba(162, 195, 164, 0.1) 1px, transparent 1px),
      linear-gradient(45deg, rgba(0, 74, 173, 0.05) 25%, transparent 25%, transparent 75%, rgba(0, 74, 173, 0.05) 75%),
      linear-gradient(135deg, rgba(0, 74, 173, 0.05) 25%, transparent 25%, transparent 75%, rgba(0, 74, 173, 0.05) 75%)
    ` : `
      radial-gradient(circle at center, rgba(0, 0, 0, 0.1) 1px, transparent 1.5px),
      radial-gradient(circle at center, rgba(0, 74, 173, 0.2) 1px, transparent 1.5px)
    `,
    backgroundSize: '20px 20px',
    backgroundPosition: '0 0, 10px 10px',
    opacity: theme.palette.mode === 'dark' ? 0.5 : 0.7,
    pointerEvents: 'none',
    zIndex: 0,
    borderRadius: 'inherit'
  }
}));

const RenderModeButton = styled(Button)(({ theme, selected }) => ({
  background: selected 
    ? '#004aad'
    : theme.palette.mode === 'dark'
      ? 'rgba(26, 26, 38, 0.95)'
      : 'rgba(0,0,0,0.05)',
  color: selected 
    ? '#fdfdfd' 
    : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.9)'
      : 'text.primary',
  backdropFilter: 'blur(8px)',
  padding: '8px 16px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  border: `1px solid ${selected 
    ? '#004aad'
    : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.08)'}`,
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  '&:hover': {
    background: selected 
      ? '#003580'
      : theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.08))'
        : 'rgba(0,0,0,0.1)',
    borderColor: selected 
      ? '#003580'
      : '#004aad',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  '&:active': {
    background: selected 
      ? '#003580'
      : theme.palette.mode === 'dark'
        ? 'rgba(26, 26, 38, 0.95)'
        : 'rgba(0,0,0,0.1)',
  }
}));

const UploadButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#004aad',
  color: '#fdfdfd',
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 600,
  boxShadow: '0 4px 14px rgba(0, 74, 173, 0.3)',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  '&:hover': {
    backgroundColor: '#003580',
    boxShadow: '0 6px 20px rgba(0, 74, 173, 0.4)',
    transform: 'translateY(-2px)',
  },
}));

// --- Workflow Title Indicator ---
const fadeInSlideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Removed progressWipe keyframes

const WorkflowTitleIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3), // Adjust spacing as needed
  right: theme.spacing(3), // Move to the right side
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 38, 0.85)' : 'rgba(253, 253, 253, 0.85)',
  backdropFilter: 'blur(8px)',
  borderRadius: '12px',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 15px rgba(0, 0, 0, 0.3)'
    : '0 4px 15px rgba(0, 74, 173, 0.15)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 74, 173, 0.1)'}`,
  zIndex: 10, // Ensure it's above other content
  animation: `${fadeInSlideDown} 0.6s cubic-bezier(0.25, 0.8, 0.25, 1) forwards`,
  opacity: 0, // Start hidden for animation
  willChange: 'transform, opacity',
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
    color: theme.palette.primary.main,
    transform: 'scale(1.1)',
  },
}));

const FastStagingChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: theme.spacing(1.5),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '8px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(0, 188, 212, 0.2), rgba(0, 150, 136, 0.2))'
    : 'linear-gradient(135deg, rgba(0, 188, 212, 0.15), rgba(0, 150, 136, 0.15))',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0, 188, 212, 0.4)' : 'rgba(0, 150, 136, 0.4)'}`,
  color: theme.palette.mode === 'dark' ? '#80deea' : '#00796b',
  fontSize: '0.75rem',
  fontWeight: 600,
  animation: `${fadeInSlideDown} 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) 0.2s forwards`, // Delayed animation
  opacity: 0,
}));

const QualityChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: theme.spacing(1.5),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '8px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(100, 181, 246, 0.2), rgba(3, 155, 229, 0.2))' // Blue gradient
    : 'linear-gradient(135deg, rgba(100, 181, 246, 0.15), rgba(3, 155, 229, 0.15))',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(100, 181, 246, 0.4)' : 'rgba(3, 155, 229, 0.4)'}`, // Blue border
  color: theme.palette.mode === 'dark' ? '#90caf9' : '#0277bd', // Blue text
  fontSize: '0.75rem',
  fontWeight: 600,
  animation: `${fadeInSlideDown} 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) 0.2s forwards`, // Delayed animation
  opacity: 0,
}));
// --- End Workflow Title Indicator ---


// Minimal Room Type Select Component for Gemini Fast Staging
const MinimalRoomTypeSelect = ({ workflow, value, onChange }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (option) => {
    onChange(option);
    handleClose();
  };

  // Define a consistent set of room options to prevent UI flickering
  const defaultRoomOptions = ['Living Room', 'Bedroom', 'Kitchen', 'Bathroom', 'Dining Room'];
  const roomOptions = workflow?.additionalInputs?.room_type?.options || defaultRoomOptions;
  const displayValue = roomTypeTranslations[value] || value || 'Seçiniz'; // Default text

  return (
    <>
      <Button
        id="minimal-room-type-button"
        aria-controls={open ? 'minimal-room-type-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          textTransform: 'none',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
          borderRadius: '8px',
          padding: '6px 12px',
          minWidth: '150px', // Adjust as needed
          justifyContent: 'space-between', // Ensure icon is pushed to the right
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {displayValue}
      </Button>
      <Menu
        id="minimal-room-type-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'minimal-room-type-button',
        }}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5, // Limit height
            width: '20ch', // Adjust width
            marginTop: '4px',
            borderRadius: '8px',
            boxShadow: theme.shadows[3],
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(40, 40, 50, 0.95)' : '#fff',
            backdropFilter: 'blur(5px)',
          },
        }}
      >
        {roomOptions.map((option) => (
          <MenuItem 
            key={option} 
            selected={option === value} 
            onClick={() => handleMenuItemClick(option)}
            sx={{ 
              fontSize: '0.9rem',
              '&.Mui-selected': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.2)' : 'rgba(0, 74, 173, 0.1)',
                fontWeight: 500,
              },
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.1)' : 'rgba(0, 74, 173, 0.05)',
              }
            }}
          >
            {roomTypeTranslations[option] || option}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Predefined colors for the minimal color picker
const predefinedColors = [
  '#FFFFFF', // White
  '#E0E0E0', // Light Grey
  '#A0A0A0', // Grey
  '#424242', // Dark Grey
  '#000000', // Black
  '#D2B48C', // Tan / Beige
  '#A0522D', // Sienna / Brown
  '#5F4C3B', // Dark Brown
  '#6495ED', // Cornflower Blue
  '#90EE90', // Light Green
  '#CD5C5C', // Indian Red
  '#FFD700', // Gold
];

// Minimal Color Picker Component
const MinimalColorPicker = ({ value, onChange }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColor, setSelectedColor] = useState(value);

  // Update local state when prop changes
  useEffect(() => {
    setSelectedColor(value);
  }, [value]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color); // Update local state immediately
    onChange(color);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'minimal-color-picker-popover' : undefined;

  return (
    <>
      <CustomTooltip title="Mobilya Rengi Seç" placement="bottom">
        <Button
          aria-describedby={id}
          onClick={handleClick}
          sx={{
            minWidth: '40px',
            height: '40px',
            padding: 0,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
            backgroundColor: selectedColor || (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'), // Show selected color from local state
            boxShadow: `inset 0 0 0 2px ${theme.palette.background.paper}`, // Inner border effect
            '&:hover': {
              borderColor: theme.palette.primary.main,
              opacity: 0.9,
              backgroundColor: selectedColor || (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'), // Keep the selected color on hover
            },
            position: 'relative', // For the overlay
          }}
        >
          {/* Optionally add an icon or indicator if no color is selected */}
          {!selectedColor && <ColorLensIcon sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />}
        </Button>
      </CustomTooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 1.5,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)', // 4 columns
            gap: 1,
            borderRadius: '10px',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(40, 40, 50, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(5px)',
            boxShadow: theme.shadows[4],
          }
        }}
      >
        {predefinedColors.map((color) => (
          <Box
            key={color}
            onClick={() => handleColorSelect(color)}
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              backgroundColor: color,
              cursor: 'pointer',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'}`,
              boxShadow: selectedColor === color ? `0 0 0 2px ${theme.palette.primary.main}` : 'none', // Highlight selected using local state
              transition: 'transform 0.1s ease-in-out, box-shadow 0.1s ease-in-out',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
              },
            }}
          />
        ))}
      </Popover>
    </>
  );
};

// Mapping from hex colors in MinimalColorPicker to Turkish keywords
const hexToTurkishColor = {
  '#FFFFFF': 'beyaz',        // White
  '#E0E0E0': 'açık gri',     // Light Grey
  '#A0A0A0': 'gri',          // Grey
  '#424242': 'koyu gri',     // Dark Grey
  '#000000': 'siyah',        // Black
  '#D2B48C': 'bej',          // Tan / Beige
  '#A0522D': 'kahverengi',   // Sienna / Brown
  '#5F4C3B': 'koyu kahverengi',// Dark Brown
  '#6495ED': 'mavi',         // Cornflower Blue
  '#90EE90': 'açık yeşil',   // Light Green
  '#CD5C5C': 'kırmızı',      // Indian Red
  '#FFD700': 'altın sarısı', // Gold
};


// Move style objects outside component
const modalStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.5)'
};

const loadingStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '400px',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: '12px'
};

// Move all style objects outside component
const containerStyles = {
  opacity: 0,
  animation: 'fadeIn 0.3s ease-out forwards',
  '@keyframes fadeIn': {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' }
  },
  willChange: 'transform, opacity'
};

const VirtualizedContainer = React.memo(({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1600px',
        minWidth: { xs: '100%', sm: '600px', md: '800px', lg: '1200px' },
        overflowX: 'hidden',
        pb: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mx: 'auto',
        position: 'relative',
        flex: 1,
        '& > *': {
          width: '100%',
          maxWidth: '100%'
        },
        [theme.breakpoints.up('sm')]: {
          width: theme.palette.mode === 'light' ? '100%' : '100%',
          minWidth: theme.palette.mode === 'light' ? '600px' : '600px'
        },
        [theme.breakpoints.up('md')]: {
          width: theme.palette.mode === 'light' ? '100%' : '100%',
          minWidth: theme.palette.mode === 'light' ? '800px' : '800px'
        },
        [theme.breakpoints.up('lg')]: {
          width: theme.palette.mode === 'light' ? '100%' : '100%',
          minWidth: theme.palette.mode === 'light' ? '1200px' : '1200px'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none'
        }
      }}
    >
      {children}
    </Box>
  );
});

// Define prompts for the 'refurnish' workflow
const refurnishPrompts = {
  'Living Room': {
    'scandinavian': 'İskandinav tarzında, açık renk ahşap ayaklı minimalist gri bir kanepe, sade bir ahşap orta sehpa ve rahat, açık renkli tekstillerle döşenmiş.',
    'parisian': 'Parisien tarzında, krem veya pastel tonlarda, kıvrımlı hatlara sahip zarif bir kanepe, oymalı detaylı veya mermer tablalı bir orta sehpa ve vintage esintili aksesuarlar içeren şık.',
    'eclectic': 'Eklektik tarzda, farklı dönem ve stillerden cesur renkli veya desenli bir kanepe, benzersiz tasarımlı bir orta sehpa ve kişisel koleksiyon objeleriyle dekore edilmiş canlı.',
    'contemporary': 'Çağdaş tarzda, alçak profilli, nötr renkli modüler bir kanepe, metal ayaklı şık cam veya ahşap bir orta sehpa ve soyut desenli bir halı içeren modern.',
    'futuristic': 'Fütüristik tarzında, organik formlara sahip, parlak beyaz veya metalik renkli bir kanepe, entegre aydınlatmalı veya akıllı özellikli bir orta sehpa ve teknolojik aksesuarlar içeren yenilikçi.',
    'minimalist': 'Minimalist tarzında, net hatlı, tek renk (genellikle beyaz, gri veya siyah) bir kanepe, fonksiyonel ve gereksiz detaylardan arındırılmış bir orta sehpa ve boşluk hissi veren.',
    'japandi': 'Japandi tarzında, İskandinav sadeliği ve Japon doğallığını birleştiren, alçak, rahat bir kanepe, doğal ahşap veya bambu bir orta sehpa ve sakin renk paleti içeren huzurlu.',
    'bohemian': 'Bohem tarzında, rahat, minderli, renkli veya etnik desenli bir kanepe, eskitme ahşap veya rattan bir orta sehpa, bol bitki ve el yapımı tekstillerle dolu özgür ruhlu.'
  },
  'Bedroom': {
    'scandinavian': 'İskandinav tarzında, açık renk ahşap çerçeveli sade bir yatak, beyaz veya pastel tonlarda nevresimler ve fonksiyonel, minimalist ahşap komodinler bulunan.',
    'parisian': 'Parisien tarzında, ferforje veya oymalı ahşap başlıklı zarif bir yatak, ipek veya saten pastel renkli nevresimler ve antika görünümlü komodinler içeren romantik.',
    'eclectic': 'Eklektik tarzında, farklı stilleri harmanlayan, belki kadife veya desenli kumaş kaplı gösterişli bir yatak başlığı, cesur renkli nevresimler ve benzersiz tasarımlı komodinler bulunan kişisel.',
    'contemporary': 'Çağdaş tarzında, entegre aydınlatmalı, kumaş veya ahşap başlıklı platform bir yatak, nötr tonlarda kaliteli nevresimler ve şık, düz hatlı komodinler içeren modern.',
    'futuristic': 'Fütüristik tarzında, yuvarlak hatlı veya havada süzülüyormuş gibi görünen bir yatak, akıllı aydınlatma sistemleri, parlak yüzeyli komodinler ve minimalist nevresimler içeren yenilikçi.',
    'minimalist': 'Minimalist tarzında, alçak platformlu, başlıksız veya çok sade başlıklı bir yatak, tek renk nevresimler ve gereksiz eşyalardan arındırılmış, fonksiyonel komodinler bulunan sakin.',
    'japandi': 'Japandi tarzında, alçak, doğal ahşap çerçeveli bir yatak, keten veya pamuklu, nötr renkli nevresimler ve bambu veya açık renk ahşap komodinler içeren dingin.',
    'bohemian': 'Bohem tarzında, ahşap veya rattan, belki gölgelikli bir yatak karyolası, kat kat tekstiller, makrome duvar süsleri ve etnik desenli nevresimlerle dolu rahat.'
  },
  'Kitchen': {
    'scandinavian': 'İskandinav tarzında mutfak adası için açık renk ahşap bar sandalyeleri, minimalist masa aksesuarları ve fonksiyonel depolama çözümleri.',
    'parisian': 'Parisien tarzında bistro tarzı küçük bir masa ve sandalyeler, mermer detaylı aksesuarlar ve zarif aydınlatma elemanları.',
    'eclectic': 'Eklektik tarzda farklı renk ve modelde sandalyeler, vintage posterler veya tablolar ve ilginç mutfak gereçleri.',
    'contemporary': 'Çağdaş tarzda şık metal veya akrilik bar sandalyeleri, modern sanat eserleri ve minimalist masa düzenlemeleri.',
    'futuristic': 'Fütüristik tarzda entegre teknolojiye sahip bar sandalyeleri, holografik veya parlak yüzeyli aksesuarlar ve akıllı aydınlatma.',
    'minimalist': 'Minimalist tarzda sade tasarımlı, fonksiyonel bar sandalyeleri, gereksiz süslemelerden arındırılmış tezgah üstü ve nötr renkli masa takımları.',
    'japandi': 'Japandi tarzında doğal ahşap veya bambu bar sandalyeleri, seramik veya taş aksesuarlar ve sakin renklerde masa örtüleri.',
    'bohemian': 'Bohem tarzda rattan veya renkli minderli ahşap sandalyeler, bol bitki, asma sepetler ve el yapımı seramikler.'
  },
  'Bathroom': {
    'scandinavian': 'İskandinav tarzında, açık renk ahşap veya beyaz, sade tasarımlı bir banyo dolabı, minimalist bir ayna ve fonksiyonel depolama kutuları.',
    'parisian': 'Parisien tarzında, kıvrımlı ayaklı veya oymalı detaylı bir lavabo ünitesi, süslü çerçeveli bir ayna ve vintage tarzı sabunluklar.',
    'eclectic': 'Eklektik tarzda, cesur renkli veya desenli bir banyo dolabı, farklı stillerde aynalar ve benzersiz aksesuarlar.',
    'contemporary': 'Çağdaş tarzında, düz hatlı, askılı modern bir banyo dolabı, büyük, çerçevesiz veya LED aydınlatmalı bir ayna ve şık, minimalist aksesuarlar.',
    'futuristic': 'Fütüristik tarzında, parlak yüzeyli veya entegre ekranlı bir banyo dolabı, akıllı ayna ve minimalist, teknolojik aksesuarlar.',
    'minimalist': 'Minimalist tarzında, fonksiyonel, duvara monte bir lavabo ünitesi, çerçevesiz sade bir ayna ve gereksiz objelerden arındırılmış raflar.',
    'japandi': 'Japandi tarzında, doğal ahşap veya bambu detaylı bir banyo dolabı, sade ahşap çerçeveli bir ayna ve taş veya seramik aksesuarlar.',
    'bohemian': 'Bohem tarzında, eskitme ahşap veya mozaik detaylı bir lavabo ünitesi, makrome veya rattan çerçeveli bir ayna, bol bitki ve renkli tekstiller.'
  },
  'Dining Room': {
    'scandinavian': 'İskandinav tarzında, açık renk ahşap, sade tasarımlı bir yemek masası ve \'wishbone\' gibi ikonik sandalyeler, minimalist bir sarkıt lamba.',
    'parisian': 'Parisien tarzında, zarif, belki mermer tablalı bir masa, kumaş döşemeli veya \'Thonet\' tarzı sandalyeler ve kristal bir avize.',
    'eclectic': 'Eklektik tarzda, farklı dönemlerden bir masa ve uyumsuz ama uyumlu sandalyeler, cesur bir avize ve duvarlarda sanat eserleri.',
    'contemporary': 'Çağdaş tarzında, cam veya açık renk ahşap tablalı, temiz hatlara sahip bir yemek masası, şık, ergonomik sandalyeler ve modern bir aydınlatma grubu.',
    'futuristic': 'Fütüristik tarzında, parlak veya şeffaf malzemeden yapılmış organik formlu bir masa ve sandalyeler, entegre aydınlatmalı veya teknolojik özellikli.',
    'minimalist': 'Minimalist tarzında, net geometrik formlu, tek renk bir masa ve sandalyeler, fonksiyonel ve süslemesiz bir aydınlatma.',
    'japandi': 'Japandi tarzında, alçak, masif ahşap bir masa, sade tasarımlı ahşap veya kumaş sandalyeler ve kağıt fener tarzı bir aydınlatma.',
    'bohemian': 'Bohem tarzında, masif, belki eskitme ahşap bir masa, rattan veya farklı renklerde sandalyeler, bol bitki ve renkli masa örtüleri.'
  }
};

const CanvasEditorContent = React.memo(({ onStageChange }) => {
  const navigate = useNavigate();
  const handleGalleryImageSelect = (imageUrl) => {
    setUploadedImageUrl(imageUrl);
    setUploadedImage(imageUrl); // Update both image URLs and object URLs
  }
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const workflowId = searchParams.get('workflow');
  const { user } = useAuth(); // Add this line to get user from auth context
  const propertyId = location.state?.propertyId;
  const createNewProperty = location.state?.createNewProperty;
  const editImage = location.state?.editImage;
  
  const initialWorkflow = useMemo(() => 
    canvasWorkflows.find(w => w.id === workflowId) || canvasWorkflows[0],
    [workflowId]
  );
  
  // Check if we have a sample project from location state
  const sampleProject = location.state?.sampleProject;
  
  const [selectedWorkflow, setSelectedWorkflow] = useState(initialWorkflow);
  const [deploymentId, setDeploymentId] = useState(initialWorkflow.deploymentId);
  const [mode, setMode] = useState(initialWorkflow.mode || 'interior');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [currentStage, setCurrentStage] = useState('workflow');
  const [completedStages, setCompletedStages] = useState([]);
  const [session, setSession] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [dimensions, setDimensions] = useState(4);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [autoEnhance, setAutoEnhance] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [originalImageForOutput, setOriginalImageForOutput] = useState(null); // State for the image used in the last generation
  const [isMaskingEnabled, setIsMaskingEnabled] = useState(false);
  const [inputCheckpoint, setInputCheckpoint] = useState('epicphotogasm_ultimateFidelity.safetensors');
  
  // Tutorial state
  const [userProfile, setUserProfile] = useState(null);
  const [showTutorials, setShowTutorials] = useState({
    workflow: false,
    style: false,
    upload: false,
    prompt: false,
    generate: false
  });
  
  // Add refs for each section
  const workflowRef = useRef(null);
  const modeRef = useRef(null);
  const styleRef = useRef(null);
  const uploadRef = useRef(null);
  const promptRef = useRef(null);
  const generateRef = useRef(null);

  // Track last scroll timestamp
  const lastScrollRef = useRef(Date.now());
  const scrollTimeoutRef = useRef(null);
  
  // Initialize options containers visibility state
  useEffect(() => {
    // Make sure all options containers are visible by default
    if (selectedWorkflow?.additionalInputs) {
      Object.keys(selectedWorkflow.additionalInputs).forEach(key => {
        const container = document.getElementById(`options-container-${key}`);
        if (container) {
          container.style.display = 'flex';
          // Apply initial animation
          container.style.animation = 'toggleExpand 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards';
          
          // Initialize toggle icons
          const toggleIcon = document.querySelector(`.toggle-icon-${key}`);
          if (toggleIcon) {
            toggleIcon.style.transform = 'rotate(0deg)';
          }
        }
      });
    }
  }, [selectedWorkflow?.additionalInputs]);
  
  // Check user profile for tutorial preferences
  useEffect(() => {
    const checkUserProfile = async () => {
      if (!user) return;
      
      try {
        const { data } = await supabase
          .from('profiles')
          .select('preferences, experience_level, dismissed_tutorials')
          .eq('id', user.id)
          .single();
          
        setUserProfile(data);
        
        // Show tutorials based on user preferences and experience level
        if (data?.preferences?.tutorials) {
          const dismissedTutorials = data.dismissed_tutorials || {};
          const experienceLevel = data.experience_level || 'beginner';
          
          // Set which tutorials to show based on experience level
          const tutorialsToShow = {
            workflow: !dismissedTutorials.workflow && experienceLevel !== 'advanced',
            style: !dismissedTutorials.style && experienceLevel === 'beginner',
            upload: !dismissedTutorials.upload && experienceLevel === 'beginner',
            prompt: !dismissedTutorials.prompt && (experienceLevel === 'beginner' || experienceLevel === 'intermediate'),
            generate: !dismissedTutorials.generate && experienceLevel === 'beginner'
          };
          
          setShowTutorials(tutorialsToShow);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    
    checkUserProfile();
  }, [user]);

  // Scroll handler function with offset and smoothing
  const scrollToSection = useCallback((stageId) => {
    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      const refs = {
        'workflow': workflowRef,
        'mode': modeRef,
        'style': styleRef,
        'upload': uploadRef,
        'prompt': promptRef,
        'generate': generateRef
      };

      const ref = refs[stageId];
      if (!ref?.current) {
        console.log(`Section ${stageId} not ready for scrolling`);
        return;
      }

      try {
        const now = Date.now();
        const timeSinceLastScroll = now - lastScrollRef.current;
        const headerOffset = 84; // Header height

        // Clear any pending scroll timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // Add a small delay if scrolls are happening in quick succession
        const scrollDelay = timeSinceLastScroll < 500 ? 200 : 0;

        scrollTimeoutRef.current = setTimeout(() => {
          if (!ref.current) return; // Double check ref is still valid

          const elementPosition = ref.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          // First scroll to bring element into view
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Then ensure proper positioning after any dynamic content loads
          setTimeout(() => {
            if (!ref.current) return; // Triple check ref is still valid
            
            const finalPosition = ref.current.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            if (Math.abs(window.scrollY - finalPosition) > 2) { // Only adjust if off by more than 2px
              window.scrollTo({
                top: finalPosition,
                behavior: 'smooth'
              });
            }
          }, 300);

          lastScrollRef.current = Date.now();
        }, scrollDelay);
      } catch (error) {
        console.warn(`Error scrolling to section ${stageId}:`, error);
      }
    }, 50); // Small delay to ensure DOM is ready
  }, []);

  // Handle dismissing a tutorial
  const handleDismissTutorial = useCallback(async (tutorialId) => {
    setShowTutorials(prev => ({
      ...prev,
      [tutorialId]: false
    }));
    
    // Save dismissed tutorial to user profile
    if (user) {
      try {
        const dismissedTutorials = userProfile?.dismissed_tutorials || {};
        dismissedTutorials[tutorialId] = true;
        
        await supabase
          .from('profiles')
          .update({
            dismissed_tutorials: dismissedTutorials
          })
          .eq('id', user.id);
      } catch (error) {
        console.error('Error saving dismissed tutorial:', error);
      }
    }
  }, [user, userProfile]);

  // Automatically select a random style for the 'refurnish' workflow on initial load
  useEffect(() => {
    if (initialWorkflow.id === 'refurnish') {
      const randomIndex = Math.floor(Math.random() * styleOptions.length);
      const randomStyle = styleOptions[randomIndex].value;
      memoizedSetSelectedStyle(randomStyle);
      // Also update the prompt based on the randomly selected style and current room type
      updateRefurnishPrompt(inputs.room_type, randomStyle);
    } else if (initialWorkflow.id === 'staging') {
      // For staging workflow, set default style
      memoizedSetSelectedStyle('contemporary');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  // Pass workflow ID to parent component for tracker filtering
  useEffect(() => {
    onStageChange?.({ 
      currentStage: currentStage, 
      completedStages: completedStages,
      workflowId: selectedWorkflow?.id
    });
  }, [selectedWorkflow?.id, currentStage, completedStages, onStageChange]);

  // Handle automatic stage progression
  // Initialize from sample project if available
  useEffect(() => {
    if (sampleProject) {
      // Set workflow based on sample project
      const workflow = canvasWorkflows.find(w => w.id === sampleProject.workflow) || selectedWorkflow;
      setSelectedWorkflow(workflow);
      setDeploymentId(workflow.deploymentId);
      
      // Set style
      if (sampleProject.style) {
        setSelectedStyle(sampleProject.style);
      } else if (workflow.defaultStyle) {
        // Use default style from workflow if available
        setSelectedStyle(workflow.defaultStyle);
      }
      
      // Set prompt
      if (sampleProject.prompt) {
        setPrompt(sampleProject.prompt);
      }
      
      // Set original image if available
      if (sampleProject.originalImage) {
        setUploadedImageUrl(sampleProject.originalImage);
        setUploadedImage(sampleProject.originalImage);
      }
      
      // Auto-complete stages for sample projects
      setCompletedStages(['workflow', 'mode', 'style']);
      if (sampleProject.originalImage) {
        setCompletedStages(prev => [...prev, 'upload']);
      }
      if (sampleProject.prompt) {
        setCompletedStages(prev => [...prev, 'prompt']);
        setCurrentStage('generate');
      }
    } else {
      // For new sessions, set default style for workflows that need it
      if (selectedWorkflow?.defaultStyle && !selectedStyle) {
        setSelectedStyle(selectedWorkflow.defaultStyle);
      }
      
      // Set default prompt for virtual-staging workflow on initial load
      if (selectedWorkflow?.id === 'virtual-staging' && !prompt) {
        setPrompt('Çağdaş tarzında, duvara karşı koltuk takımı, ortada masa, altında halı, saksı bitkisi, oturma odası dekorasyonu, duvarda kanvas tablo.');
      }
    }
  }, [sampleProject, selectedWorkflow, prompt]);

  // Automatically enable masking when 'inpaint' workflow is selected and an image is uploaded
  useEffect(() => {
    // Removed automatic enabling: User must click the button now.
    // if (selectedWorkflow?.id === 'inpaint' && uploadedImage && !isMaskingEnabled) {
    //   setIsMaskingEnabled(true); 
    // }
    // If workflow changes away from inpaint, disable masking
    if (selectedWorkflow?.id !== 'inpaint' && isMaskingEnabled) {
      setIsMaskingEnabled(false);
    }
  }, [selectedWorkflow, uploadedImage, isMaskingEnabled]); // Remove setInputs and setPrompt from dependencies

  useEffect(() => {
    const updateStages = () => {
      let newCompletedStages = [...completedStages];
      let newCurrentStage = currentStage;

      // Initial workflow selection
      if (selectedWorkflow && !newCompletedStages.includes('workflow')) {
        newCompletedStages.push('workflow');
        newCurrentStage = 'mode';
        
        // Auto-complete mode stage since it's hidden
        if (!newCompletedStages.includes('mode')) {
          newCompletedStages.push('mode');
          newCurrentStage = 'style';
        }
      }

      // Mode selection - auto-complete since it's hidden
      if (newCompletedStages.includes('workflow') && !newCompletedStages.includes('mode')) {
        newCompletedStages.push('mode');
        newCurrentStage = 'style';
      }

      // Style selection
      if (newCompletedStages.includes('mode') && !newCompletedStages.includes('style')) {
        // Auto-complete style for virtual-staging, inpaint, and furniture-removal workflows
        if (selectedWorkflow?.id === 'virtual-staging' || selectedWorkflow?.id === 'inpaint' || 
            selectedWorkflow?.id === 'furniture-removal') {
          newCompletedStages.push('style');
          newCurrentStage = 'upload';
        }
        // For staging workflow, require style reference image
        else if (selectedWorkflow?.id === 'staging') {
          if (inputs.input_styleimage) {
            newCompletedStages.push('style');
            newCurrentStage = 'upload';
          } else {
            newCurrentStage = 'style'; // Stay on style stage until reference image is uploaded
          }
        }
        // For other workflows, require style selection
        else if (selectedStyle) {
          // For Refurnishing workflow, require style reference image
          if (selectedWorkflow?.requiresStyleImage && !inputs.input_styleimage) {
            // Don't complete style stage without reference image
          } else {
            newCompletedStages.push('style');
            newCurrentStage = 'upload';
          }
        }
      }

      // Upload completion
      if ((uploadedImage || uploadedImageUrl) && newCompletedStages.includes('style') && !newCompletedStages.includes('upload')) {
        newCompletedStages.push('upload');
        newCurrentStage = 'prompt';
      }

      // Prompt completion - Auto-complete for fast-staging and furniture-removal, otherwise check prompt length
      if (newCompletedStages.includes('upload') && !newCompletedStages.includes('prompt')) {
        if (selectedWorkflow?.id === 'fast-staging' || selectedWorkflow?.id === 'furniture-removal') {
          newCompletedStages.push('prompt'); // Auto-complete for Gemini workflows that don't need prompts
          newCurrentStage = 'generate';
        } else if (prompt && prompt.trim().length > 10) {
          newCompletedStages.push('prompt'); // Complete based on prompt input for others
          newCurrentStage = 'generate';
        }
      }

      // Generate completion
      if (generatedImageUrl && newCompletedStages.includes('prompt') && !newCompletedStages.includes('generate')) {
        newCompletedStages.push('generate');
      }

      // Update states if changed
      if (newCompletedStages.length !== completedStages.length) {
        setCompletedStages(newCompletedStages);
      }
      if (newCurrentStage !== currentStage) {
        setCurrentStage(newCurrentStage);
        scrollToSection(newCurrentStage);
      }

      // Notify parent of stage changes
      onStageChange?.({ 
        currentStage: newCurrentStage, 
        completedStages: newCompletedStages,
        workflowId: selectedWorkflow?.id
      });
    };

    updateStages();
  }, [
    selectedWorkflow,
    initialWorkflow,
    mode,
    selectedStyle,
    uploadedImage,
    uploadedImageUrl,
    prompt,
    generatedImageUrl,
    completedStages,
    currentStage,
    onStageChange
  ]);

  const handleStageClick = useCallback((stageId) => {
    // Only allow clicking completed stages or the current stage
    const stageIndex = stages.findIndex(s => s.id === stageId);
    const currentIndex = stages.findIndex(s => s.id === currentStage);
    
    if (completedStages.includes(stageId) || stageIndex === currentIndex) {
      scrollToSection(stageId);
    }
  }, [currentStage, completedStages, scrollToSection]);

  const completeCurrentStage = useCallback(() => {
    // Only complete a stage if it hasn't been completed yet
    if (completedStages.includes(currentStage)) {
      return;
    }

    // Validate current stage completion with stricter conditions
    const canComplete = (() => {
      switch(currentStage) {
        case 'workflow':
          return selectedWorkflow && selectedWorkflow !== initialWorkflow;
        case 'mode':
          return mode && mode !== initialWorkflow.mode;
        case 'style':
          return selectedStyle && selectedStyle !== '';
        case 'upload':
          return (uploadedImage !== null || uploadedImageUrl !== null);
        case 'prompt':
          return prompt && prompt.trim().length > 10; // Require meaningful prompt
        case 'generate':
          return generatedImageUrl !== null; // Only complete after successful generation
        default:
          return false;
      }
    })();

    if (canComplete) {
      setCompletedStages(prev => [...prev, currentStage]);
      
      // Move to next stage
      const currentIndex = stages.findIndex(s => s.id === currentStage);
      if (currentIndex < stages.length - 1) {
        const nextStage = stages[currentIndex + 1].id;
        setCurrentStage(nextStage);
        scrollToSection(nextStage);
      }
    }
  }, [currentStage, completedStages, selectedWorkflow, initialWorkflow, mode, selectedStyle, uploadedImage, uploadedImageUrl, prompt, generatedImageUrl, scrollToSection]);
  // Memoize all state setters
  const memoizedSetMode = useCallback((value) => {
    if (value !== mode) {  // Only if actually changing
      setMode(value);
      if (currentStage === 'mode') {
        completeCurrentStage();
      }
    }
  }, [completeCurrentStage, currentStage, mode]);

  const memoizedSetSelectedWorkflow = useCallback((workflow) => {
    if (workflow.id !== selectedWorkflow?.id) {
      setSelectedWorkflow(workflow);
      setDeploymentId(workflow.deploymentId);
      setMode(workflow.mode);
      
      // Set default style if available
      if (workflow.defaultStyle) {
        setSelectedStyle(workflow.defaultStyle);
      }
      
      // Update masking state based on new workflow
      setIsMaskingEnabled(workflow.id === 'selective-transform');
      
      // Set default prompt for virtual-staging workflow
      if (workflow.id === 'virtual-staging') {
        setPrompt('Çağdaş tarzında, duvara karşı koltuk takımı, ortada masa, altında halı, saksı bitkisi, oturma odası dekorasyonu, duvarda kanvas tablo.');
      }
      
      // Initialize removal intensity for furniture removal workflow
      if (workflow.id === 'furniture-removal') {
        setInputs(prev => ({
          ...prev,
          removal_intensity: 'complete' // Default to complete removal
        }));
      }
      
      // Force workflow stage completion
      if (!completedStages.includes('workflow')) {
        setCompletedStages(prev => [...prev, 'workflow']);
        setCurrentStage('mode');
        scrollToSection('mode');
      }
    }
  }, [completeCurrentStage, currentStage, selectedWorkflow, setSelectedStyle]);

  const memoizedSetSelectedStyle = useCallback((value) => {
    if (value !== selectedStyle) {  // Only if actually changing
      setSelectedStyle(value);
      if (currentStage === 'style') {
        completeCurrentStage();
      }
    }
  }, [completeCurrentStage, currentStage, selectedStyle]);
  const memoizedSetAutoEnhance = useCallback((value) => setAutoEnhance(value), []);
  const memoizedSetIsMaskingEnabled = useCallback((value) => setIsMaskingEnabled(value), []);
  const memoizedSetUploadedImage = useCallback((value) => setUploadedImage(value), []);
  const memoizedSetPrompt = useCallback((value) => setPrompt(value), []); // Keep original memoizedSetPrompt

  // Helper function to update prompt for refurnish workflow
  const updateRefurnishPrompt = useCallback((roomType, style) => {
    if (!roomType || !style) return; // Ensure both are selected
    const newPrompt = refurnishPrompts[roomType]?.[style] || '';
    if (newPrompt) {
      memoizedSetPrompt(newPrompt); // Use the memoized setter
    }
  }, [memoizedSetPrompt]); // Add dependency

  const memoizedSetShowCompare = useCallback((value) => {
    setShowCompare(value);
  }, []);
  const dimensionOptions = [
    { value: 0, label: '1536 x 640 (landscape)', width: 1536, height: 640 },
    { value: 1, label: '1344 x 768 (landscape)', width: 1344, height: 768 },
    { value: 2, label: '1216 x 832 (landscape)', width: 1216, height: 832 },
    { value: 3, label: '1152 x 896 (landscape)', width: 1152, height: 896 },
    { value: 4, label: '1024 x 1024 (square)', width: 1024, height: 1024 },
    { value: 5, label: '896 x 1152 (portrait)', width: 896, height: 1152 },
    { value: 6, label: '832 x 1216 (portrait)', width: 832, height: 1216 },
    { value: 7, label: '768 x 1344 (portrait)', width: 768, height: 1344 },
    { value: 8, label: '640 x 1536 (portrait)', width: 640, height: 1536 },
  ];
  const checkpoints = [
    'epicphotogasm_ultimateFidelity.safetensors',
    'deliberate_v3.safetensors'  // Example second checkpoint
  ];
  const fileInputRef = useRef(null);
  const theme = useTheme();

  const styleOptions = [
    { value: 'scandinavian', label: 'İskandinav' },
    { value: 'parisian', label: 'Paris' },
    { value: 'eclectic', label: 'Eklektik' },
    { value: 'contemporary', label: 'Çağdaş' },
    { value: 'futuristic', label: 'Fütüristik' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: 'japandi', label: 'Japandi' },
    { value: 'bohemian', label: 'Bohem' }
  ];

  // Memoize image upload handler with all dependencies, including HEIC conversion
  const handleImageUpload = useCallback(async (inputFile) => {
    if (!inputFile) return;

    // --- Size Check ---
    if (inputFile.size > MAX_UPLOAD_SIZE_BYTES) {
      alert(`Dosya boyutu çok büyük (${(inputFile.size / 1024 / 1024).toFixed(1)} MB). Lütfen ${MAX_UPLOAD_SIZE_MB} MB'dan küçük bir dosya yükleyin.`);
      // Optionally reset file input if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      return; // Stop processing if file is too large
    }

    let fileToProcess = inputFile;
    let originalFileName = inputFile.name;
    let conversionError = null; // Track conversion errors

    // Show loading overlay immediately
    const uploadLoadingOverlay = document.querySelector('.upload-loading-overlay');
    if (uploadLoadingOverlay) {
      uploadLoadingOverlay.classList.add('active');
      console.log("Upload overlay activated for:", originalFileName);
    } else {
      console.warn("Upload overlay element not found.");
    }

    try {
      // --- HEIC/HEIF Conversion Step ---
      const fileNameLower = inputFile?.name?.toLowerCase() || '';
      const isHeicExtension = fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif');
      const isHeicType = inputFile.type === 'image/heic' || inputFile.type === 'image/heif';

      if (isHeicType || (!inputFile.type && isHeicExtension)) {
        console.log(`Attempting HEIC/HEIF to JPEG conversion for: ${originalFileName}`);
        try {
          const arrayBuffer = await inputFile.arrayBuffer();
          console.log(`Read ${originalFileName} into ArrayBuffer (${arrayBuffer.byteLength} bytes)`);
          const uint8Array = new Uint8Array(arrayBuffer); // Convert ArrayBuffer to Uint8Array
          console.log(`Converted ArrayBuffer to Uint8Array`);

          const outputBuffer = await convert({
            buffer: uint8Array, // Pass Uint8Array to the convert function
            format: 'JPEG',
            quality: 0.9
          });
          console.log(`Conversion successful. Output buffer size: ${outputBuffer.byteLength} bytes`);

          const jpegFileName = originalFileName.replace(/\.(heic|heif)$/i, '.jpg');
          fileToProcess = new File([outputBuffer], jpegFileName, { type: 'image/jpeg' });
          console.log(`Created new File object for converted JPEG: ${jpegFileName}`);

        } catch (convError) {
          console.error(`HEIC conversion failed for ${originalFileName}:`, convError);
          conversionError = convError; // Store the error
          // Keep fileToProcess as the original inputFile if conversion fails
          fileToProcess = inputFile;
          alert(`HEIC dosyası JPEG'e dönüştürülemedi: ${convError.message}. Orijinal dosya yüklenecek.`);
        }
      } else {
        console.log(`File ${originalFileName} is not HEIC/HEIF, proceeding with direct upload.`);
      }

      // --- Upload Step ---
      // Ensure loading overlay is active before upload (might have been deactivated if conversion failed)
      if (uploadLoadingOverlay && !uploadLoadingOverlay.classList.contains('active')) {
          uploadLoadingOverlay.classList.add('active');
          console.log("Reactivated upload overlay before upload for:", fileToProcess.name);
      }

      console.log(`Preparing to upload file: ${fileToProcess.name}, Type: ${fileToProcess.type}, Size: ${fileToProcess.size}`);
      const fileName = `${Date.now()}-${fileToProcess.name}`;
      const { data, error: uploadError } = await supabase.storage
        .from('uploaded-images')
        .upload(fileName, fileToProcess); // Use the potentially converted file

      if (uploadError) {
        console.error(`Supabase upload failed for ${fileName}:`, uploadError);
        throw uploadError; // Re-throw upload error
      }
      console.log(`Successfully uploaded ${fileName} to Supabase.`);

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('uploaded-images')
        .getPublicUrl(data.path);

      // Create object URL from the (potentially converted) file
      const objectUrl = URL.createObjectURL(fileToProcess); // Use fileToProcess here

      // Batch state updates
      requestAnimationFrame(() => {
        setUploadedImageUrl(publicUrl);
        setUploadedImage(objectUrl);
        setOriginalImageForOutput(null);

        // Hide loading overlay ONLY on success
        if (uploadLoadingOverlay) {
          uploadLoadingOverlay.classList.remove('active');
          console.log("Upload overlay deactivated on success for:", fileToProcess.name);
        }
      });

      // Cleanup object URL
      completeCurrentStage(); // Complete upload stage
      return () => URL.revokeObjectURL(objectUrl);
    } catch (error) { // Catches errors from upload or re-thrown conversion errors
      console.error("Error during image processing or upload:", error);
      // Hide loading overlay on any error
      if (uploadLoadingOverlay) {
        uploadLoadingOverlay.classList.remove('active');
         console.log("Upload overlay deactivated on error for:", originalFileName);
      }
      // Show specific error message
      const message = conversionError
        ? `HEIC dönüştürme başarısız oldu: ${conversionError.message}`
        : `Görsel yüklenemedi: ${error.message}`;
      alert(message);
    }
  }, [setUploadedImageUrl, setUploadedImage, completeCurrentStage]); // Removed setIsMaskingEnabled as it's not directly used here

  const throttledImageUpload = useMemo(
    () => throttle(handleImageUpload, 300),
    [handleImageUpload]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    setIsDragging(false);
    const file = e.dataTransfer.files[0];

    // --- Size Check ---
    if (file && file.size > MAX_UPLOAD_SIZE_BYTES) {
      alert(`Dosya boyutu çok büyük (${(file.size / 1024 / 1024).toFixed(1)} MB). Lütfen ${MAX_UPLOAD_SIZE_MB} MB'dan küçük bir dosya yükleyin.`);
      return; // Stop processing if file is too large
    }

    const fileNameLower = file?.name?.toLowerCase() || '';
    const isHeic = fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif');
    const isAvif = fileNameLower.endsWith('.avif') || file?.type === 'image/avif';
    const isGif = fileNameLower.endsWith('.gif') || file?.type === 'image/gif'; // Check for GIF

    // Define allowed types (excluding GIF and AVIF)
    const isAllowedType = file?.type.startsWith('image/') && !isAvif && !isGif;
    const isAllowedHeic = file?.type === 'image/heic' || file?.type === 'image/heif' || (!file?.type && isHeic);

    // Allow standard image types (excluding GIF/AVIF), HEIC/HEIF by type, or HEIC/HEIF by extension if type is missing/wrong
    if (file && (isAllowedType || isAllowedHeic)) {
      handleImageUpload(file);
    } else if (file) {
      // Log the actual type received for debugging
      console.warn(`Unsupported file type dropped: Name='${file.name}', Type='${file.type}'`);
      let message = `Desteklenmeyen dosya türü: ${file.type || file.name}. Lütfen JPEG, PNG, WebP, HEIC veya HEIF dosyası yükleyin.`;
      if (isAvif) {
        message = `AVIF dosyaları şu anda desteklenmiyor. Lütfen farklı bir format deneyin.`;
      } else if (isGif) {
        message = `Animasyonlu GIF dosyaları desteklenmiyor. Lütfen statik bir resim formatı (JPEG, PNG, WebP, HEIC) deneyin.`;
      }
      alert(message);
    }
  }, [handleImageUpload, setIsDragging]);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files[0];

    // --- Size Check ---
    if (file && file.size > MAX_UPLOAD_SIZE_BYTES) {
      alert(`Dosya boyutu çok büyük (${(file.size / 1024 / 1024).toFixed(1)} MB). Lütfen ${MAX_UPLOAD_SIZE_MB} MB'dan küçük bir dosya yükleyin.`);
      // Reset file input to allow selecting the same file again if needed
      e.target.value = null;
      return; // Stop processing if file is too large
    }

    const fileNameLower = file?.name?.toLowerCase() || '';
    const isHeic = fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif');
    const isAvif = fileNameLower.endsWith('.avif') || file?.type === 'image/avif';
    const isGif = fileNameLower.endsWith('.gif') || file?.type === 'image/gif'; // Check for GIF

    // Define allowed types (excluding GIF and AVIF)
    const isAllowedType = file?.type.startsWith('image/') && !isAvif && !isGif;
    const isAllowedHeic = file?.type === 'image/heic' || file?.type === 'image/heif' || (!file?.type && isHeic);

    // Allow standard image types (excluding GIF/AVIF), HEIC/HEIF by type, or HEIC/HEIF by extension if type is missing/wrong
    if (file && (isAllowedType || isAllowedHeic)) {
      handleImageUpload(file);
    } else if (file) {
      // Log the actual type received for debugging
      console.warn(`Unsupported file type selected: Name='${file.name}', Type='${file.type}'`);
      let message = `Desteklenmeyen dosya türü: ${file.type || file.name}. Lütfen JPEG, PNG, WebP, HEIC veya HEIF dosyası yükleyin.`;
      if (isAvif) {
        message = `AVIF dosyaları şu anda desteklenmiyor. Lütfen farklı bir format deneyin.`;
      } else if (isGif) {
        message = `Animasyonlu GIF dosyaları desteklenmiyor. Lütfen statik bir resim formatı (JPEG, PNG, WebP, HEIC) deneyin.`;
      }
      alert(message);
    }
    // Reset file input to allow selecting the same file again if needed
    e.target.value = null;
  }, [handleImageUpload]);

  const throttledDragOver = useMemo(
    () => throttle(handleDragOver, 100),
    [handleDragOver]
  );

  const throttledDrop = useMemo(
    () => throttle(handleDrop, 100),
    [handleDrop]
  );

  const throttledFileInput = useMemo(
    () => throttle(handleFileInput, 100),
    [handleFileInput]
  );
  const memoizedHandleDragLeave = useMemo(() => handleDragLeave, [handleDragLeave]);
  const memoizedHandleDrop = useMemo(() => handleDrop, [handleDrop]);
  const memoizedHandleFileInput = useMemo(() => handleFileInput, [handleFileInput]);
  
  const [uploadedMaskUrl, setUploadedMaskUrl] = useState(null);
  const [maskRenderTrigger, setMaskRenderTrigger] = useState(Date.now()); // Add state for re-render trigger
  const [showCompare, setShowCompare] = useState(false);
  const [showMaskSuccess, setShowMaskSuccess] = useState(false);
  const [showCreditAlert, setShowCreditAlert] = useState(false);
  const [creditAlertData, setCreditAlertData] = useState({ required: 0, available: 0 });
  const [newGalleryImage, setNewGalleryImage] = useState(null); // State for the newly generated image
  const [isRoomFurnished, setIsRoomFurnished] = useState(false); // Default to false (Mobilya Temizle)
  const [anchorEl, setAnchorEl] = useState(null); // For dropdown menus
  const [inputs, setInputs] = useState(() => ({
    room_type: 'Living Room',
    style: 'contemporary',
    color_theme: 'original',
    furniture_color: null, // Initialize furniture color state
    input_styleimage: '',
    input_mask: '',
    input_room: '/private_models/unet/livingroom.pth',
    removal_intensity: 'complete' // Default to complete removal
  }));
  
  // Define room-specific prompt templates
  const roomPromptTemplates = useMemo(() => ({
    'Living Room': 'duvara karşı koltuk takımı, ortada masa, altında halı, saksı bitkisi, duvarda kanvas tablo',
    'Bedroom': 'yatak, komodinler, gardırop, gece lambaları, yatak örtüsü, yastıklar',
    'Kitchen': 'mutfak dolapları, tezgah, ocak, buzdolabı, yemek masası, sandalyeler, mutfak aksesuarları',
    'Bathroom': 'lavabo, duş kabini, klozet, banyo dolabı, ayna, havlular, banyo aksesuarları',
    'Dining Room': 'yemek masası, sandalyeler, büfe, avize, servis tabakları'
  }), []);

  // Auto-select color and generate prompt for virtual-staging workflow
  useEffect(() => {
    if ((selectedWorkflow?.id === 'virtual-staging' || selectedWorkflow?.id === 'fast-staging' || selectedWorkflow?.id === 'staging') && uploadedImage) {
      // Set default color theme
      setInputs(prev => ({
        ...prev,
        color_theme: 'original',
        room_type: prev.room_type || 'Living Room',
        style: prev.style || 'contemporary'
      }));
      
      // Generate dynamic prompt based on current selections
      updateDynamicPrompt(inputs.room_type, inputs.style, inputs.furniture_color);
    }
  }, [selectedWorkflow, uploadedImage]);

  // Function to update prompt based on room type, style, and color
  const updateDynamicPrompt = useCallback((roomType, style, color) => {
    if (!roomType || !style) return;
    
    const roomTemplate = roomPromptTemplates[roomType] || roomPromptTemplates['Living Room'];
    const styleTr = styleTranslations[style] || style || 'Çağdaş';
    const roomTypeTr = roomTypeTranslations[roomType] || roomType || 'oturma odası';
    const colorKeyword = color ? hexToTurkishColor[color] : null;
    
    // Modify template if color is selected
    let modifiedTemplate = roomTemplate;
    if (colorKeyword && roomType === 'Living Room') {
      modifiedTemplate = roomTemplate.replace('koltuk takımı', `${colorKeyword} koltuk takımı`);
    } else if (colorKeyword && roomType === 'Bedroom') {
      modifiedTemplate = roomTemplate.replace('yatak örtüsü', `${colorKeyword} yatak örtüsü`);
    } else if (colorKeyword && roomType === 'Dining Room') {
      modifiedTemplate = roomTemplate.replace('sandalyeler', `${colorKeyword} sandalyeler`);
    }
    
    // Construct the final prompt
    const newPrompt = `${styleTr} tarzında, ${modifiedTemplate}.`; // Removed roomTypeTr dekorasyonu
    setPrompt(newPrompt);
    
    // Translate the prompt to English for API
    const translatePrompt = async () => {
      try {
        const translatedPrompt = await translationService.translateAndEnhance(newPrompt, 'TR_TO_EN', { enhance: false });
        
        // Ensure we store the translated *string* in roomPromptEn
        if (translatedPrompt && translatedPrompt.translatedText) {
          setInputs(prev => ({
            ...prev,
            roomPromptEn: translatedPrompt.translatedText // Store only the translated string
          }));
          console.log("Prompt translated to English:", translatedPrompt.translatedText);
        } else if (translatedPrompt && typeof translatedPrompt === 'string') {
           // Handle cases where translation might return just a string (less likely with current service)
           setInputs(prev => ({ ...prev, roomPromptEn: translatedPrompt }));
           console.log("Prompt translated to English (string fallback):", translatedPrompt);
        }
      } catch (error) {
        console.error("Error translating prompt:", error);
      }
    };
    
    translatePrompt();
  }, [roomPromptTemplates, styleTranslations, roomTypeTranslations]);

  const handleSaveMask = (mask, maskUrl) => {
    console.log('Mask saved:', maskUrl);
    // Append timestamp to bust cache
    const timestampedUrl = `${maskUrl}?t=${Date.now()}`;
    setUploadedMaskUrl(timestampedUrl);
    setIsMaskingEnabled(false);
    setShowMaskSuccess(true);
    setTimeout(() => setShowMaskSuccess(false), 2000);
    // Update the render trigger state after other updates
    setMaskRenderTrigger(Date.now());
  };

  // Memoize the entire form section
  // Memoize modal content
  // Define formStyles inside component
  const formStyles = useMemo(() => ({
    mb: 4,
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(26, 26, 37, 0.8)'
      : '#ffffff',
    transition: 'background-color 0.3s ease',
    padding: { xs: 3, sm: 4 },
    px: { xs: 4, sm: 6 },
    borderRadius: 3,
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 4px 24px rgba(0, 0, 0, 0.2)' 
      : '0 4px 24px rgba(0, 0, 0, 0.06)',
    backdropFilter: 'blur(8px)',
    width: '100%',    '&::before': {
      opacity: 0.05
    }
  }), [theme.palette.mode]);

  const memoizedModalContent = useMemo(() => (
    showCompare && uploadedImageUrl && generatedImageUrl && (
      <ImageCompare
        originalImage={uploadedImageUrl}
        generatedImage={generatedImageUrl}
        onClose={() => setShowCompare(false)}
      />
    )
  ), [showCompare, uploadedImageUrl, generatedImageUrl]);

  // --- Helper function to save metadata ---
  // Moved outside the onClick handler for better practice
  const saveMetadataToDatabase = useCallback(async (metadata, apiPrompt, thumbnailUrl) => { // Added thumbnailUrl
    if (!metadata || !metadata.url) {
      console.warn("No generation URL available, skipping metadata save.");
      return;
    }
    
    // Remove room_type if this is a furniture removal workflow
    if (metadata.workflow_mode === 'furniture-removal' && metadata.room_type) {
      delete metadata.room_type;
    }

    try {
      // --- Property Info Logic ---
      const propertyInfo = {};
      if (editImage && editImage.property_id) {
        propertyInfo.property_id = editImage.property_id;
        propertyInfo.property_name = editImage.property_name;
      } else if (propertyId) {
        const { data: propertyData } = await supabase
          .from('properties')
          .select('name')
          .eq('id', propertyId)
          .single();
        if (propertyData) {
          propertyInfo.property_id = propertyId;
          propertyInfo.property_name = propertyData.name;
        } else {
          const { data: imagePropertyData } = await supabase
            .from('images')
            .select('property_name')
            .eq('property_id', propertyId)
            .limit(1);
          if (imagePropertyData && imagePropertyData.length > 0) {
            propertyInfo.property_id = propertyId;
            propertyInfo.property_name = imagePropertyData[0].property_name;
          }
        }
      } else if (createNewProperty) {
        if (location.state?.propertyId) {
          propertyInfo.property_id = location.state.propertyId;
          propertyInfo.property_name = location.state.propertyName || "Yeni Mülk";
        } else {
          const newPropertyId = uuidv4();
          await supabase
            .from('properties')
            .insert([{
              id: newPropertyId,
              name: "Yeni Mülk",
              user_id: user.id, // Ensure user is accessible here
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }]);
          propertyInfo.property_id = newPropertyId;
          propertyInfo.property_name = "Yeni Mülk";
        }
      }
      // --- End Property Info Logic ---

      // Ensure the correct prompt is in the metadata before saving
      const finalMetadataToSave = {
        ...metadata,
        // Use the prompt that was actually sent to the API
        prompt: apiPrompt,
        thumbnail_url: thumbnailUrl, // Add thumbnail URL here
        ...propertyInfo
      };

      const imageDataToSave = {
        ...finalMetadataToSave // Use the final metadata object
      };

      console.log("Saving image metadata to DB:", JSON.stringify(imageDataToSave, null, 2));
      const { data: imageRecord, error: dbError } = await supabase
        .from('images')
        .insert([imageDataToSave])
        .select();

      if (dbError) {
        throw dbError;
      }

      // Update state to trigger gallery refresh
      if (imageRecord && imageRecord.length > 0) {
        setNewGalleryImage(imageRecord[0]); // Ensure setNewGalleryImage is accessible
        console.log("Image metadata saved successfully:", imageRecord[0]);
      }
    } catch (dbSaveError) {
      console.error("Error saving image metadata to database:", dbSaveError);
      // Optionally show a warning to the user that metadata saving failed
    }
  }, [user, editImage, propertyId, createNewProperty, location, supabase, uuidv4, setNewGalleryImage]); // Add dependencies
  // --- End Helper function ---


  const renderForm = useMemo(() => (
    <StyledBox sx={containerStyles} ref={workflowRef}>
      {/* Mod Seçimi section hidden as requested */}
      <Box ref={modeRef}></Box>

      <Box ref={styleRef}>
        {showTutorials.style && (
          <TutorialTip
            id="style"
            title="Stil Seçimi"
            description="Görselleştirmeniz için bir tasarım stili seçin. Bu, yapay zekanın oluşturacağı görselin genel estetiğini belirler."
            placement="top"
            onDismiss={() => handleDismissTutorial('style')}
          >
            <Box sx={{ width: '100%', height: 1, mb: 2 }} />
          </TutorialTip>
        )}
        <StyledBox sx={{
          mt: { xs: 8, sm: 10 }, // Reduced margin top to decrease space below indicator
          mb: 4,
          backgroundColor: 'transparent',
          padding: { xs: 3, sm: 4 },
          px: { xs: 4, sm: 6 },
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 4px 24px rgba(0, 0, 0, 0.2)' 
            : '0 4px 24px rgba(0, 0, 0, 0.06)',
          width: '100%',
          '&::before': {
            opacity: 0.05
          }
        }}>
          {/* Removed accordion-style options */}
          
          {/* Stil Seçimi section shown for all workflows */}
          {true && (
            <>
              {/* Minimal Room Type Dropdown for Gemini Fast Staging, Staging, Virtual Staging and other workflows */}
              {(selectedWorkflow?.id === 'fast-staging' || selectedWorkflow?.id === 'staging' || selectedWorkflow?.id === 'virtual-staging' || selectedWorkflow?.id === 'refurnish' || selectedWorkflow?.id === 'inpaint' || selectedWorkflow?.id === 'furniture-removal') && (
                <Box sx={{ 
                  mb: 3, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  flexWrap: 'wrap' // Allow items to wrap on smaller screens
                }}>
                  {selectedWorkflow?.id !== 'furniture-removal' && (
                    <>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500,
                        color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
                        minWidth: '80px' // Ensure label doesn't wrap awkwardly
                      }}>
                        Oda Tipi:
                      </Typography>
                      <MinimalRoomTypeSelect 
                        workflow={selectedWorkflow} 
                        value={inputs.room_type} 
                        onChange={(newValue) => {
                          setInputs(prev => ({
                            ...prev,
                            room_type: newValue
                          }));
                          // Update prompt when room type changes
                          updateDynamicPrompt(newValue, inputs.style, inputs.furniture_color);
                        }}
                      />
                    </>
                  )}
                  
                  {/* Removal Intensity Selector for Furniture Removal workflow - Hidden from top bar */}
                  {selectedWorkflow?.id === 'furniture-removal' && (
                    <>
                      {/* Removal intensity dropdown completely removed from top bar */}
                    </>
                  )}
                  {/* Add Minimal Color Picker - Only show if NOT furniture removal workflow */}
                  {selectedWorkflow?.id !== 'furniture-removal' && (
                    <MinimalColorPicker
                      value={inputs.furniture_color}
                      onChange={(newColor) => {
                        setInputs(prev => ({
                          ...prev,
                          furniture_color: newColor
                        }));
                        // Update prompt when color changes
                        updateDynamicPrompt(inputs.room_type, inputs.style, newColor);
                      }}
                    />
                  )}
                  {/* Furnished Room Toggle Button - Only show if NOT furniture removal workflow */}
                  {selectedWorkflow?.id !== 'furniture-removal' && (
                    <CustomTooltip title={isRoomFurnished ? "Mobilya Temizleme Aktif" : "Mobilya Temizle"} placement="bottom">
                      <IconButton
                        onClick={() => {
                          setIsRoomFurnished(prev => {
                            const newValue = !prev;
                            // Update prompt when furnished state changes
                            if (selectedWorkflow && (selectedWorkflow.id === 'virtual-staging' || selectedWorkflow.id === 'fast-staging' || selectedWorkflow.id === 'staging')) {
                              updateDynamicPrompt(inputs.room_type, inputs.style, inputs.furniture_color);
                            }
                            return newValue;
                          });
                        }}
                        sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
                          backgroundColor: isRoomFurnished 
                            ? theme.palette.primary.main + '30' // Light primary background when active
                            : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                          color: isRoomFurnished 
                            ? theme.palette.primary.main 
                            : theme.palette.text.secondary,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: isRoomFurnished 
                              ? theme.palette.primary.main + '40'
                              : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)'),
                          },
                          transition: 'all 0.2s ease-in-out',
                          position: 'relative', // For better icon positioning
                        }}
                      >
                        <ChairIcon sx={{ 
                          fontSize: '1.2rem',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }} />
                      </IconButton>
                    </CustomTooltip>
                  )}
                </Box>
              )}

              {selectedWorkflow?.id !== 'furniture-removal' && (
                <>
                  <Typography variant="subtitle1" sx={{
                    mb: 2, 
                    fontWeight: 500,
                    color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'inherit'
                  }}>
                    Stil Seçimi
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 1, sm: 2 }, 
                    mb: 3, 
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(26, 26, 37, 0.95)'
                      : '#ffffff',
                    transition: 'background-color 0.3s ease-in-out',
                    padding: { xs: 2, sm: 3 },
                    borderRadius: '12px',
                    boxShadow: theme.palette.mode === 'dark' 
                      ? '0 4px 24px rgba(0, 0, 0, 0.2)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.05)',
                    backdropFilter: 'blur(8px)',
                    flexWrap: 'wrap',
                    '& .MuiButton-root': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 12px', sm: '10px 20px' },
                      minHeight: { xs: '36px', sm: 'auto' }
                    }
                  }}>
                {styleOptions.map((style) => {
                  // Get corresponding icon for each style
                  const getStyleIcon = (styleValue) => {
                    switch(styleValue) {
                      case 'scandinavian': return <StarIcon />;
                      case 'parisian': return <LocationCityIcon />;
                      case 'eclectic': return <PaletteIcon />;
                      case 'contemporary': return <ApartmentIcon />;
                      case 'futuristic': return <RocketIcon />;
                      case 'minimalist': return <CropSquareIcon />;
                      case 'japandi': return <GrassIcon />;
                      case 'bohemian': return <LocalFloristIcon />;
                      case 'indian': return <span style={{ 
                        color: theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad',
                        filter: 'grayscale(100%)'
                      }}>🕌</span>; // Monochrome Taj Mahal icon matching theme colors
                      default: return <AutoFixHighIcon />;
                    }
                  };

                  return (
                    <RenderModeButton
                      key={style.value}
                      selected={selectedStyle === style.value}
                      onClick={() => {
                        memoizedSetSelectedStyle(style.value);
                        // If refurnish workflow, update prompt based on new style and current room type
                        if (selectedWorkflow.id === 'refurnish') {
                          updateRefurnishPrompt(inputs.room_type, style.value);
                        } else if (selectedWorkflow.id === 'virtual-staging' || selectedWorkflow.id === 'fast-staging' || selectedWorkflow.id === 'staging') {
                          // Update prompt for staging workflows
                          updateDynamicPrompt(inputs.room_type, style.value, inputs.furniture_color);
                        
                          // Also update the style in inputs state to ensure it's used in API calls
                          setInputs(prev => ({
                            ...prev,
                            style: style.value
                          }));
                        }
                      }}
                      startIcon={getStyleIcon(style.value)}
                    >
                      {styleTranslations[style.value]}
                    </RenderModeButton>
                  );
                })}
                  </Box>
                  <Typography variant="body2" sx={{
                    color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
                  }}>
                    Render çıktınız için bir stil seçin
                  </Typography>
                </>
              )}
              
              {/* Furniture Removal Description */}
              {selectedWorkflow?.id === 'furniture-removal' && (
                <Box sx={{ 
                  mb: 3, 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(26, 26, 37, 0.95)'
                    : '#ffffff',
                  transition: 'background-color 0.3s ease-in-out',
                  padding: { xs: 2, sm: 3 },
                  borderRadius: '12px',
                  boxShadow: theme.palette.mode === 'dark' 
                    ? '0 4px 24px rgba(0, 0, 0, 0.2)' 
                    : '0 2px 8px rgba(0, 0, 0, 0.05)',
                  backdropFilter: 'blur(8px)',
                }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>Mobilya Temizleme Yoğunluğu:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: inputs.removal_intensity === 'light' 
                        ? theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.15)' : 'rgba(0, 74, 173, 0.05)'
                        : 'transparent',
                      border: inputs.removal_intensity === 'light' ? `1px solid ${theme.palette.primary.main}` : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.1)' : 'rgba(0, 74, 173, 0.03)'
                      }
                    }}
                    onClick={() => setInputs(prev => ({ ...prev, removal_intensity: 'light' }))}>
                      <Box sx={{ 
                        width: { xs: 20, sm: 24 }, 
                        height: { xs: 20, sm: 24 }, 
                        minWidth: { xs: 20, sm: 24 },
                        borderRadius: '50%', 
                        border: `2px solid ${inputs.removal_intensity === 'light' ? theme.palette.primary.main : theme.palette.text.secondary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {inputs.removal_intensity === 'light' && (
                          <Box sx={{ 
                            width: { xs: 10, sm: 12 }, 
                            height: { xs: 10, sm: 12 }, 
                            borderRadius: '50%', 
                            backgroundColor: theme.palette.primary.main 
                          }} />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Hafif</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Küçük eşyaları ve dağınıklığı temizler, ana mobilyaları korur
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: inputs.removal_intensity === 'medium' 
                        ? theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.15)' : 'rgba(0, 74, 173, 0.05)'
                        : 'transparent',
                      border: inputs.removal_intensity === 'medium' ? `1px solid ${theme.palette.primary.main}` : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.1)' : 'rgba(0, 74, 173, 0.03)'
                      }
                    }}
                    onClick={() => setInputs(prev => ({ ...prev, removal_intensity: 'medium' }))}>
                      <Box sx={{ 
                        width: { xs: 20, sm: 24 }, 
                        height: { xs: 20, sm: 24 }, 
                        minWidth: { xs: 20, sm: 24 },
                        borderRadius: '50%', 
                        border: `2px solid ${inputs.removal_intensity === 'medium' ? theme.palette.primary.main : theme.palette.text.secondary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {inputs.removal_intensity === 'medium' && (
                          <Box sx={{ 
                            width: { xs: 10, sm: 12 }, 
                            height: { xs: 10, sm: 12 }, 
                            borderRadius: '50%', 
                            backgroundColor: theme.palette.primary.main 
                          }} />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Orta</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Çoğu mobilyayı kaldırır, sadece temel yapısal elemanları korur
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: inputs.removal_intensity === 'complete' || !inputs.removal_intensity
                        ? theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.15)' : 'rgba(0, 74, 173, 0.05)'
                        : 'transparent',
                      border: inputs.removal_intensity === 'complete' || !inputs.removal_intensity ? `1px solid ${theme.palette.primary.main}` : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 74, 173, 0.1)' : 'rgba(0, 74, 173, 0.03)'
                      }
                    }}
                    onClick={() => setInputs(prev => ({ ...prev, removal_intensity: 'complete' }))}>
                      <Box sx={{ 
                        width: { xs: 20, sm: 24 }, 
                        height: { xs: 20, sm: 24 }, 
                        minWidth: { xs: 20, sm: 24 },
                        borderRadius: '50%', 
                        border: `2px solid ${inputs.removal_intensity === 'complete' || !inputs.removal_intensity ? theme.palette.primary.main : theme.palette.text.secondary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {(inputs.removal_intensity === 'complete' || !inputs.removal_intensity) && (
                          <Box sx={{ 
                            width: { xs: 10, sm: 12 }, 
                            height: { xs: 10, sm: 12 }, 
                            borderRadius: '50%', 
                            backgroundColor: theme.palette.primary.main 
                          }} />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Tam</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Tüm mobilyaları, dekorasyonları ve kişisel eşyaları tamamen temizler
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </StyledBox>

        {(selectedWorkflow?.requiresStyleImage || selectedWorkflow?.id === 'staging') && (
          <>
            <Alert severity="warning" sx={{ 
              mb: 2, 
              textAlign: 'center',
              backgroundColor: theme => theme.palette.mode === 'dark' 
                ? 'rgba(237, 108, 2, 0.1)' 
                : 'rgba(237, 108, 2, 0.05)',
              border: '1px solid',
              borderColor: 'warning.main',
              borderRadius: '8px',
              '& .MuiAlert-icon': {
                color: 'warning.main'
              }
            }}>
              {selectedWorkflow?.id === 'staging' 
                ? 'Stil transferi için bir referans görsel yüklemeniz gerekiyor. Bu adım zorunludur.'
                : `Lütfen bir referans resim ekleyin. Bu yükleme ${selectedWorkflow.title} için zorunludur.`}
            </Alert>
            <Box sx={{ 
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
            mt: 3
          }}>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file || !file.type.startsWith('image/')) return;
                    
                  try {
                    // Show loading animation in style preview box
                    const stylePreviewBox = document.querySelector('.style-image-preview').parentElement;
                    const styleLoadingOverlay = document.createElement('div');
                    styleLoadingOverlay.style.cssText = `
                      position: absolute;
                      top: 0;
                      left: 0;
                      right: 0;
                      bottom: 0;
                      background: rgba(0, 0, 0, 0.5);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      border-radius: 12px;
                      z-index: 1000;
                    `;
                    
                    const spinner = document.createElement('div');
                    spinner.style.cssText = `
                      width: 40px;
                      height: 40px;
                      border: 3px solid rgba(0, 74, 173, 0.3);
                      border-top: 3px solid #004aad;
                      border-radius: 50%;
                      animation: spin 1s linear infinite;
                    `;
                    
                    styleLoadingOverlay.appendChild(spinner);
                    stylePreviewBox.appendChild(styleLoadingOverlay);

                    const fileName = `style-${Date.now()}-${file.name}`;
                    const { data, error } = await supabase.storage
                      .from('uploaded-images')
                      .upload(fileName, file);

                    if (error) throw error;

                    const { data: { publicUrl } } = supabase.storage
                      .from('uploaded-images')
                      .getPublicUrl(data.path);

                    // Create object URL for preview
                    const objectUrl = URL.createObjectURL(file);
                    
                    // Create a unique URL with timestamp to prevent caching
                    const timestampedUrl = `${publicUrl}?t=${Date.now()}`;
                    
                    console.log('New style image uploaded, URL:', timestampedUrl);
                    
                    // Store the URL in a global variable to ensure it's accessible
                    window.latestStyleImageUrl = timestampedUrl;
                    
                    // Define a function to update the state to ensure consistency
                    const updateStyleImage = () => {
                      setInputs(prev => {
                        const newInputs = {
                          ...prev,
                          input_styleimage: timestampedUrl
                        };
                        console.log('Style image URL updated in state:', timestampedUrl);
                        return newInputs;
                      });
                    };
                    
                    // Update state immediately
                    updateStyleImage();
                    
                    // Force multiple updates to ensure it takes effect
                    setTimeout(updateStyleImage, 50);
                    setTimeout(updateStyleImage, 200);
                    setTimeout(updateStyleImage, 500);

                    // Remove loading animation from style preview
                    styleLoadingOverlay.remove();

                    // Show success message or preview
                    console.log('Style image uploaded successfully:', publicUrl);
                      
                    // Update UI to show the uploaded image with fade effect
                    const previewElement = document.querySelector('.style-image-preview');
                    if (previewElement) {
                      // Fade out current image
                      previewElement.style.opacity = '0';
                      
                      // After fade out, update the source and fade in
                      setTimeout(() => {
                        previewElement.src = objectUrl;
                        previewElement.style.display = 'block';
                        
                        // Fade in the new image
                        setTimeout(() => {
                          previewElement.style.opacity = '1';
                        }, 50);
                      }, 300);
                    }

                  } catch (error) {
                    console.error("Failed to upload style image:", error);
                    alert('Failed to upload style image. Please try again.');
                  }
                }}
                style={{ display: 'none' }}
                id="style-image-input"
              />
              <Box sx={{ 
                flex: '0 0 300px',
                height: '300px',
                border: '1px dashed rgba(0, 74, 173, 0.4)',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(0, 74, 173, 0.05)'
                  : 'rgba(203, 241, 153, 0.1)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(0, 74, 173, 0.1)'
                    : 'rgba(203, 241, 153, 0.2)',
                  borderColor: 'rgba(0, 74, 173, 0.6)'
                },
                mx: 'auto',
                position: 'relative',
                overflow: 'hidden',
                '& .loading-overlay': {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.7)',
                  zIndex: 2,
                  '&.active': {
                    display: 'flex'
                  }
                },
                '& .loading-spinner': {
                  width: '40px',
                  height: '40px',
                  border: '3px solid rgba(0, 74, 173, 0.3)',
                  borderTop: '3px solid #004aad',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                },
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}>
              <div className="loading-overlay">
                <div className="loading-spinner" />
              </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file || !file.type.startsWith('image/')) return;
                      
                    try {
                      // Show loading overlay
                      const loadingOverlay = document.querySelector('.loading-overlay');
                      if (loadingOverlay) {
                        loadingOverlay.classList.add('active');
                      }

                      const fileName = `style-${Date.now()}-${file.name}`;
                      const { data, error } = await supabase.storage
                        .from('uploaded-images')
                        .upload(fileName, file);

                      if (error) throw error;

                      const publicUrl = getPublicUrl('uploaded-images', data.path);
                      
                      // Create a unique URL with timestamp to prevent caching
                      const timestampedUrl = `${publicUrl}?t=${Date.now()}`;
                      
                      // Create object URL for preview
                      const objectUrl = URL.createObjectURL(file);
                      
                      console.log('New style image uploaded, URL:', timestampedUrl);
                      
                      // Store the URL in a global variable to ensure it's accessible
                      window.latestStyleImageUrl = timestampedUrl;
                      
                      // Immediately update inputs state with the new URL to prevent stale data
                      // Use a function to ensure we're working with the latest state
                      const updateInputs = () => {
                        setInputs(prev => {
                          const newInputs = {
                            ...prev,
                            input_styleimage: timestampedUrl
                          };
                          console.log('Style image URL updated in state:', timestampedUrl);
                          return newInputs;
                        });
                      };
                      
                      // Update state immediately
                      updateInputs();
                      
                      // Force multiple updates to ensure it takes effect
                      setTimeout(updateInputs, 50);
                      setTimeout(updateInputs, 200);
                      setTimeout(updateInputs, 500);

                      // Show preview and hide loading
                      const previewElement = document.querySelector('.style-image-preview');
                      if (previewElement) {
                        // Fade out current image
                        previewElement.style.opacity = '0';
                        
                        // After fade out, update the source and fade in
                        setTimeout(() => {
                          previewElement.src = objectUrl;
                          previewElement.style.display = 'block';
                          
                          // Fade in the new image
                          setTimeout(() => {
                            previewElement.style.opacity = '1';
                          }, 50);
                        }, 300);
                      }
                      
                      // Hide loading overlay
                      const styleLoadingOverlay = document.querySelector('.loading-overlay');
                      if (styleLoadingOverlay) {
                        styleLoadingOverlay.classList.remove('active');
                      }

                    } catch (error) {
                      // Hide loading on error
                      const loadingOverlay = document.querySelector('.loading-overlay');
                      if (loadingOverlay) {
                        loadingOverlay.classList.remove('active');
                      }
                      console.error("Failed to upload style image:", error);
                      alert('Failed to upload style image. Please try again.');
                    }
                  }}
                  style={{ display: 'none' }}
                  id="style-image-input"
                />
                <img 
                  className="style-image-preview"
                  alt="Style preview"
                  style={{
                    display: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    transition: 'opacity 0.3s ease'
                  }}
                />
                <Box sx={{ 
                  position: 'relative',
                  textAlign: 'center',
                  p: 2
                }}>
                  <PaletteIcon sx={{ fontSize: 40, color: 'rgba(0, 74, 173, 0.8)', mb: 1.5 }} />
                  <Typography 
                    variant="subtitle2" 
                    display="block" 
                    sx={{ 
                      color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
                      mb: 1.5,
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    {selectedWorkflow?.id === 'staging' 
                      ? 'Stil Transferi Referansı Ekle'
                      : 'Stil Referansı Ekle'}
                  </Typography>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => document.getElementById('style-image-input').click()}
                    sx={{
                      backgroundColor: '#004aad',
                      color: '#fdfdfd',
                      fontWeight: 600,
                      padding: '8px 24px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 14px rgba(0, 74, 173, 0.3)',
                      '&:hover': {
                        backgroundColor: '#003580',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0, 74, 173, 0.4)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
                    }}
                  >
                    Görsel Seç
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {!uploadedImage ? (
        <Box sx={{ mb: 4, px: { xs: 4, sm: 6 }, width: '100%' }} ref={uploadRef}>
          {showTutorials.upload && (
            <TutorialTip
              id="upload"
              title="Görsel Yükleme"
              description="Düzenlemek istediğiniz görseli buraya yükleyin. Dosyayı sürükleyip bırakabilir veya dosya seçme düğmesini kullanabilirsiniz."
              placement="top"
              onDismiss={() => handleDismissTutorial('upload')}
            >
              <Box sx={{ width: '100%', height: 1, mb: 2 }} />
            </TutorialTip>
          )}
          <Typography variant="subtitle1" sx={{ 
            mb: 2, 
            color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#000000',
            fontWeight: 500,
            fontSize: '1rem',
            letterSpacing: '-0.01em'
          }}>
            Düzenlenecek Görseli Yükle
          </Typography>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,.heic,.heif" // Explicitly list supported types, excluding GIF and AVIF
            onChange={handleFileInput}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          <UploadArea
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            isDragging={isDragging}
            sx={{
              transform: 'translateZ(0)',
              willChange: 'transform, opacity',
              transition: 'all 0.2s ease-out',
              position: 'relative',
              '& .upload-loading-overlay': {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '20px',
                zIndex: 2,
                '&.active': {
                  display: 'flex'
                }
              },
              '& .loading-spinner': {
                width: '50px',
                height: '50px',
                border: '4px solid rgba(0, 74, 173, 0.3)',
                borderTop: '4px solid #004aad',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              },
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          >
            <div className="upload-loading-overlay">
              <div className="loading-spinner" />
            </div>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#004aad',
                borderRadius: '8px',
                textTransform: 'none',
                mb: 2,
                fontWeight: 600,
                boxShadow: '0 4px 14px rgba(0, 74, 173, 0.3)',
                '&:hover': {
                  backgroundColor: '#003580',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0, 74, 173, 0.4)'
                },
                transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
              }}
            >
              Görsel Yükle
            </Button>
            <Typography variant="body2" sx={{
              color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
              fontSize: '0.875rem',
              marginTop: '8px',
              '&.MuiTypography-root': {
                color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
              }
            }}>
              veya sürükleyip bırakın (Maks. {MAX_UPLOAD_SIZE_MB} MB)
            </Typography>
          </UploadArea>
        </Box>
      ) : (
        <Box sx={{ mb: 3, px: { xs: 4, sm: 6 } }}>
          {isMaskingEnabled ? (
            <Box sx={{
              width: '100%',
              height: '90vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: '12px',
              margin: '20px 0',
              zIndex: 4
            }}>
              <MaskingCanvas
                image={uploadedImage}
                onClose={() => setUploadedImage(null)}
                onSaveMask={handleSaveMask}
              />
            </Box>
          ) : (
            // Apply conditional styling to the preview container
            <Box sx={{ 
              position: 'relative', 
              maxWidth: '50%', // Increased size by 25% (40% * 1.25 = 50%)
              mx: 'auto', // Center the smaller preview
              mb: 3 // Add some bottom margin
            }}>
              <Box sx={{ position: 'relative' }}>
                <img 
                  src={uploadedImage} 
                  alt="Uploaded"
                  style={{ 
                    width: '100%', 
                    height: 'auto',
                    borderRadius: '8px',
                    maxHeight: '600px',
                    objectFit: 'contain'
                  }} 
                />
                {uploadedMaskUrl && (
                  <Box
                    key={maskRenderTrigger} // Use trigger state as key
                    component="img"
                    src={uploadedMaskUrl} // Keep timestamped URL here
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      zIndex: 2,
                      mixBlendMode: 'overlay',
                      backgroundColor: 'transparent',
                      filter: 'hue-rotate(-10deg) brightness(1.3) contrast(0.8) saturate(0.6)',
                      animation: 'maskFloat 2.5s ease-in-out infinite',
                      opacity: 0.85,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(circle at 50% 50%, rgba(0, 74, 173, 0.3), transparent 70%)',
                        mixBlendMode: 'overlay',
                        animation: 'gradientPulse 2.5s ease-in-out infinite'
                      },
                      '@keyframes maskFloat': {
                        '0%': {
                          transform: 'translateY(0px)',
                          filter: 'hue-rotate(-10deg) brightness(1.0) contrast(0.8) saturate(0.3)'
                        },
                        '50%': {
                          transform: 'translateY(-2px)',
                          filter: 'hue-rotate(0deg) brightness(1.3) contrast(1.1) saturate(0.3)'
                        },
                        '100%': {
                          transform: 'translateY(0px)',
                          filter: 'hue-rotate(-10deg) brightness(1.7) contrast(0.8) saturate(0.3)'
                        }
                      },
                      '@keyframes gradientPulse': {
                        '0%': { opacity: 0.4 },
                        '50%': { opacity: 0.6 },
                        '100%': { opacity: 0.4 }
                      }
                    }}
                  />
                )}
              </Box>
              <Box sx={{ 
                position: 'absolute', 
                top: 10, 
                right: 10, 
                display: 'flex',
                gap: 1
              }}>
                {/* Only show Masking button for 'inpaint' workflow */}
                {selectedWorkflow?.id === 'inpaint' && (
                  <Button
                    variant="contained"
                    onClick={() => setIsMaskingEnabled(true)}
                    sx={{
                      backgroundColor: 'rgba(0, 74, 173, 0.9)',
                    '&:hover': { 
                      backgroundColor: '#003580',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 74, 173, 0.3)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <span role="img" aria-label="mask">🎭</span>
                    {uploadedMaskUrl ? 'Maskeyi Düzenle' : 'Maskelemeyi Etkinleştir'}
                  </Button>
                )}
                {uploadedMaskUrl && (
                  <Button
                    variant="contained"
                    onClick={() => setUploadedMaskUrl(null)}
                    sx={{
                      backgroundColor: 'rgba(255, 0, 0, 0.6)',
                      '&:hover': { backgroundColor: 'rgba(255, 0, 0, 0.8)' },
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    Maskeyi Sil
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={() => {
                    setUploadedImage(null);
                    setUploadedImageUrl(null); // Also clear the URL
                    setUploadedMaskUrl(null);
                    setOriginalImageForOutput(null); // Reset the output display's original image
                  }}
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  ✖️
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Conditionally render Prompt section only if not Gemini Fast Staging or Furniture Removal */}
      {selectedWorkflow?.id !== 'fast-staging' && selectedWorkflow?.id !== 'furniture-removal' && (
        <Box sx={{ mb: 4, px: { xs: 4, sm: 6 }, width: '100%' }} ref={promptRef}>
          {showTutorials.prompt && (
            <TutorialTip
              id="prompt"
            title="İstek Yazımı"
            description="Görselde görmek istediğiniz detayları açıklayın. Ne kadar detaylı olursanız, sonuç o kadar iyi olacaktır. Renk, stil, mobilya ve diğer öğeleri belirtebilirsiniz."
            placement="top"
            onDismiss={() => handleDismissTutorial('prompt')}
          >
            <Box sx={{ width: '100%', height: 1, mb: 2 }} />
          </TutorialTip>
        )}
        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            label="İstek"
            placeholder="Görselde görmek istediklerinizi açıklayın"
            value={prompt}
            onChange={(e) => memoizedSetPrompt(e.target.value)}
            onBlur={() => {
              if (prompt && prompt.trim().length > 10 && currentStage === 'prompt') {
                completeCurrentStage();
              }
            }}
            multiline
            minRows={2}
            maxRows={8}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: '80px',
                alignItems: 'flex-start',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(0, 74, 173, 0.2)',
                '&:hover': {
                  border: '1px solid rgba(0, 74, 173, 0.4)',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
                '&.Mui-focused': {
                  border: '1px solid #004aad',
                  boxShadow: '0 0 0 2px rgba(0, 74, 173, 0.2)',
                }
              },
              '& .MuiInputBase-input': {
                overflowY: 'auto !important',
                maxHeight: { xs: '200px', sm: '300px' },
                color: theme => theme.palette.mode === 'dark' ? '#E0E0E5' : '#333',
                padding: { xs: '8px 12px', sm: '20px' },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: '1.8',
                letterSpacing: '0.01em',
                wordSpacing: '0.05em',
              },
              '& .MuiInputLabel-root': {
                color: theme => theme.palette.mode === 'dark' ? '#E0E0E5' : '#666',
                marginLeft: '8px',
                '&.Mui-focused': {
                  color: '#004aad'
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'absolute', right: 16, top: 16 }}>
            <CustomTooltip title="Yapay zeka ile isteği geliştir" placement="left">
              <IconButton
                sx={{
                  color: '#004aad',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 74, 173, 0.2)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  '&.loading': {
                    pointerEvents: 'none',
                    animation: 'spin 1s linear infinite',
                    '& span': {
                      opacity: 0.5
                    }
                  },
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
                onClick={async () => {
                  try {
                    // Show loading state
                    const button = document.querySelector('.enhance-prompt-button');
                    if (button) {
                      button.classList.add('loading');
                    }
                    
                    const enhancedPrompt = await enhancePrompt(prompt, uploadedImage, selectedStyle, mode);
                    setPrompt(enhancedPrompt);
                  } catch (error) {
                    console.error('Failed to enhance prompt:', error);
                  } finally {
                    // Hide loading state
                    const button = document.querySelector('.enhance-prompt-button');
                    if (button) {
                      button.classList.remove('loading');
                    }
                  }
                }}
                className="enhance-prompt-button"
              >
                <span role="img" aria-label="enhance">✨</span>
              </IconButton>
            </CustomTooltip>
            <CustomTooltip title={autoEnhance ? "Otomatik geliştirme açık" : "Otomatik geliştirme kapalı"} placement="left">
              <IconButton
                sx={{
                  color: autoEnhance ? '#004aad' : 'rgba(0, 74, 173, 0.4)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 74, 173, 0.2)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  border: autoEnhance ? '2px solid #004aad' : '2px solid rgba(0, 74, 173, 0.2)'
                }}
                onClick={() => memoizedSetAutoEnhance(!autoEnhance)}
              >
                <span role="img" aria-label="auto-enhance">🔄</span>
              </IconButton>
              </CustomTooltip>
            </Box>
          </Box>
        </Box>
      )} 
      {/* End of conditional Prompt section */}

      {(generatedImageUrl || loading) && (
        <ErrorBoundary fallback={<div>Error loading output display</div>}>
          <Suspense fallback={
            <Box sx={loadingStyle}>
              <CircularProgress />
            </Box>
          }>
            {/* Conditionally render only if there's an image for output or loading */}
            {/* Render CanvasOutputDisplay only when loading or generated image exists */}
            {(loading || generatedImageUrl) && (
              <CanvasOutputDisplay
                originalImage={originalImageForOutput} // Pass original image for comparison modal
                generatedImage={generatedImageUrl}
                maskImage={uploadedMaskUrl} // Pass mask image if available
                isLoading={loading}
                progress={progress}
                // onDownload, onShare are handled internally now or passed if needed
              onCompare={() => setShowCompare(true)}
              onDownload={(imageUrl) => {
                const link = document.createElement('a');
                link.href = imageUrl;
                link.download = 'generated-image.png';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              onShare={(imageUrl) => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Generated Image',
                    text: 'Check out this AI-generated image!',
                    url: imageUrl,
                  });
                } else {
                  navigator.clipboard.writeText(imageUrl);
                  // You might want to show a notification that the URL was copied
                }
              }}
              onZoom={(imageUrl) => {
                // Handle zoom functionality - you might want to add a modal state
              }}
            />
          )}
          </Suspense>
        </ErrorBoundary>
      )}

      <Box sx={{ 
        px: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        mb: 4
      }} ref={generateRef}>
        {showTutorials.generate && (
          <TutorialTip
            id="generate"
            title="Görsel Oluşturma"
            description="Tüm ayarları tamamladıktan sonra 'Oluştur' düğmesine tıklayarak yapay zeka tarafından görselinizin oluşturulmasını başlatabilirsiniz."
            placement="top"
            onDismiss={() => handleDismissTutorial('generate')}
          >
            <Box sx={{ width: '100%', height: 1, mb: 2 }} />
          </TutorialTip>
        )}
        {selectedWorkflow?.requireMaskBeforeGenerate && !uploadedMaskUrl && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2, 
              width: '100%', 
              maxWidth: '600px',
              borderRadius: '12px',
              backgroundColor: theme => theme.palette.mode === 'dark' 
                ? 'rgba(237, 108, 2, 0.1)' 
                : 'rgba(237, 108, 2, 0.05)',
              border: '1px solid',
              borderColor: 'warning.main',
              '& .MuiAlert-icon': {
                color: 'warning.main'
              }
            }}
          >
            Lütfen önce maskeleme yapın ve kaydedin. Görsel üzerinde değişiklik yapmak istediğiniz alanları seçmeniz gerekiyor. İsteğinizin seçtiğiniz bölgeyle alakalı olduğundan emin olun.
          </Alert>
        )}
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ 
            position: 'relative', 
            height: '100%',
            width: '100%'
          }}>
            <GenerateButton
              loading={loading} // Pass loading state
              progress={progress} // Pass progress state
              disabled={
                loading ||
                (selectedWorkflow?.requiresStyleImage && !inputs.input_styleimage) ||
                (selectedWorkflow?.requireMaskBeforeGenerate && !uploadedMaskUrl)
              }
              onClick={async () => {
          // Capture the current original image URL for the output display *before* starting generation
          setOriginalImageForOutput(uploadedImageUrl);

          setLoading(true);
          setProgress(0);
          setGeneratedImageUrl(null); // Clear previous result
          setShowCreditAlert(false); // Hide any previous credit alert

          try {
            // --- User and Credit Check ---
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
              throw new Error('Failed to authenticate user. Please log in.');
            }

            const requiredCredits = parseInt(selectedWorkflow.credits || '10', 10); // Get credits from workflow definition, ensure integer
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('credits_remaining')
              .eq('id', user.id)
              .single();

            if (profileError) {
              throw new Error('Failed to check user credits.');
            }

            if (profile.credits_remaining < requiredCredits) {
              setCreditAlertData({ required: requiredCredits, available: profile.credits_remaining });
              setShowCreditAlert(true);
              setLoading(false); // Stop loading
              return; // Stop execution
            }

            // --- Consume Credits ---
            await consumeCredits(user.id, requiredCredits);
            console.log(`Consumed ${requiredCredits} credits for workflow: ${selectedWorkflow.title}`);

            // --- Scroll to generation area ---
            generateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // --- Pre-process: Remove furniture if needed ---
            let imageUrlForGeneration = uploadedImageUrl; // Start with the original uploaded image URL
            const needsFurnitureRemoval = (selectedWorkflow.id === 'virtual-staging' || selectedWorkflow.id === 'staging') && isRoomFurnished;

            if (needsFurnitureRemoval) {
              console.log("Initiating Gemini furniture removal pre-processing...");
              const removalPrompt = "Edit the image and Remove all furniture from the room. Remove couch, rug, nightstands, bed, paintings, sofa, lamp, cabinets, appliances, vanity, coffee table and all furnishings.";
              try {
                // Update progress for the removal step (e.g., 10% to 40%)
                const removalResult = await runGeminiMaskFreeEdit(
                  removalPrompt,
                  uploadedImageUrl, // Use the original uploaded image
                  (prog, _, message) => {
                    setProgress(10 + prog * 30); // Map 0-1 progress to 10%-40% range
                    console.log(`Furniture Removal Progress: ${10 + prog * 30}% - ${message}`);
                  }
                );
                imageUrlForGeneration = removalResult.publicUrl; // Use the edited image URL for the next step
                console.log("Furniture removal successful. Using image:", imageUrlForGeneration);
                // Update progress after removal is complete
                setProgress(40); 
              } catch (removalError) {
                console.error("Gemini furniture removal failed:", removalError);
                // Decide how to handle: stop, or proceed with original image?
                // Let's throw an error to stop the process for now.
                throw new Error(`Mobilya temizleme adımı başarısız oldu: ${removalError.message}`);
              }
            } else {
               // If no removal needed, set progress past the removal stage
               setProgress(10); 
            }
            // --- End Furniture Removal ---


            // --- Auto-enhance prompt if enabled ---
            let finalPrompt = prompt; // Start with the current prompt in the text field

            // --- Auto-enhance prompt if enabled ---
            if (autoEnhance && selectedWorkflow?.id !== 'virtual-staging') { // Don't auto-enhance the structured virtual-staging prompt
              try {
                const enhanced = await enhancePrompt(prompt, uploadedImage, selectedStyle, mode);
                finalPrompt = enhanced;
                setPrompt(enhanced); // Update the UI
                console.log("Prompt enhanced:", finalPrompt);
              } catch (enhanceError) {
                console.warn("Prompt enhancement failed, using original prompt:", enhanceError);
              }
            }

            // --- Construct/Modify prompt specifically for virtual-staging and staging ---
            let translatedApiPrompt = null; // Variable to hold the English prompt for the API
            if (selectedWorkflow?.id === 'virtual-staging' || selectedWorkflow?.id === 'staging') {
              const style = styleTranslations[inputs.style] || inputs.style || 'Çağdaş';
              const roomType = roomTypeTranslations[inputs.room_type] || inputs.room_type || 'oturma odası';
              const colorKeyword = hexToTurkishColor[inputs.furniture_color];

              // Base description elements
              const baseElements = 'duvara karşı koltuk takımı, ortada masa, altında halı, saksı bitkisi, duvarda kanvas tablo';

              // Construct the furniture description part, adding color if selected
              let furnitureDesc = colorKeyword ? `${colorKeyword} koltuk takımı` : 'koltuk takımı';
              const elementsWithColor = baseElements.replace('koltuk takımı', furnitureDesc);

              // Translate the *user's current prompt* from the state to English for the API input `positive`
              console.log(`Translating user prompt for ${selectedWorkflow.id} API (TR): ${prompt}`);
              try {
                // Use the 'prompt' state variable which holds the user's input
                const translationResult = await translationService.translateAndEnhance(prompt, 'TR_TO_EN', { enhance: false });
                // Use translatedText which should be the direct translation
                translatedApiPrompt = translationResult?.translatedText || prompt; // Fallback to original user prompt if translation fails
                console.log(`Translated user prompt for ${selectedWorkflow.id} API (EN):`, translatedApiPrompt);
              } catch (error) {
                console.error(`Error translating user prompt for ${selectedWorkflow.id}:`, error);
                translatedApiPrompt = prompt; // Fallback to original user prompt on error
              }
            }
            // --- End prompt translation ---


            // --- Determine Generation Method (ComfyDeploy vs Gemini) ---
            const isGeminiWorkflow = selectedWorkflow.deploymentId === 'gemini-image-generation';

            let generatedUrl = null; // Variable to hold the final URL (Supabase or direct)
            let generationMetadata = {}; // Object to hold metadata for DB saving
            let actualGeminiPrompt = ''; // Variable to store the prompt returned by Gemini API

            if (isGeminiWorkflow) {
              // --- Gemini Generation ---
              console.log("Initiating Gemini workflow...");

              // Handle furniture removal workflow specifically
              if (selectedWorkflow.id === 'furniture-removal') {
                console.log("Initiating Furniture Removal workflow...");
                
                // Construct removal prompt based on intensity
                let removalPrompt = "Edit the image and remove all furniture from the room.";
                const removalIntensity = inputs.removal_intensity || 'complete';
                
                switch (removalIntensity) {
                  case 'light':
                    removalPrompt = "Edit the image and remove small items and clutter from the room, keeping major furniture pieces. Remove decorations, personal items, and small objects.";
                    break;
                  case 'medium':
                    removalPrompt = "Edit the image and remove most furniture from the room, keeping only essential structural elements. Remove couches, tables, chairs, decorations, but keep built-in cabinets and fixtures.";
                    break;
                  case 'complete':
                  default:
                    removalPrompt = "Edit the image and completely remove all furniture, decorations, and personal items from the room. Create a clean, empty space with only walls, floors, windows, and built-in fixtures.";
                    break;
                }
                
                // Add room type context if available
                if (inputs.room_type) {
                  removalPrompt += ` This is a ${inputs.room_type.toLowerCase()}.`;
                }
                
                console.log("Using furniture removal prompt:", removalPrompt);
                
                try {
                  const removalResult = await runGeminiMaskFreeEdit(
                    removalPrompt,
                    uploadedImageUrl,
                    (prog, _, message) => {
                      setProgress(prog * 100); // Map 0-1 progress to 0-100% range
                      console.log(`Furniture Removal Progress: ${prog * 100}% - ${message}`);
                    }
                  );
                  
                  generatedUrl = removalResult.publicUrl;
                  setGeneratedImageUrl(generatedUrl);
                  actualGeminiPrompt = removalPrompt;
                  
                  // Create and upload thumbnail
                  const thumbnailUrl = await createAndUploadThumbnail(generatedUrl);
                  
                  // Prepare metadata
                  generationMetadata = {
                    url: generatedUrl,
                    prompt: actualGeminiPrompt,
                    thumbnail_url: thumbnailUrl,
                    user_id: user.id,
                    original_image_url: uploadedImageUrl,
                    mode: selectedWorkflow.mode,
                    checkpoint: 'gemini-2.0-flash-exp-image-generation',
                    workflow_mode: selectedWorkflow.id,
                    workflow_title: selectedWorkflow.title,
                    workflow_version: selectedWorkflow.version,
                    // Only include removal_intensity for furniture removal workflow
                    removal_intensity: removalIntensity
                  };
                } catch (removalError) {
                  console.error("Furniture removal failed:", removalError);
                  throw new Error(`Mobilya temizleme işlemi başarısız oldu: ${removalError.message}`);
                }
              } else {
                // Regular Gemini staging workflow
                // Prepare options for Gemini
                const geminiOptions = {
                  roomType: inputs.room_type || 'Living Room', // Get from inputs state
                  style: inputs.style || selectedStyle || 'contemporary', // Get from inputs or selectedStyle state
                  furnitureColor: inputs.furniture_color, // Pass the selected furniture color
                  isRoomFurnished: isRoomFurnished, // Pass the furnished state
                  // Add other options if needed
                };

              // The geminiApi.js function constructs the detailed prompt based on options.
              // We pass finalPrompt here mainly for potential metadata logging within the API function.
              console.log("Passing user prompt (for metadata) and options to Gemini API:", finalPrompt, geminiOptions);

              // Destructure the result to get both URL and the actual prompt used
              const { publicUrl: geminiResultUrl, imageGenPrompt: actualGeminiPrompt } = await runGeminiImageGeneration(
                finalPrompt, // Pass the user's potentially enhanced prompt (used for metadata in API)
                imageUrlForGeneration, // Use the potentially pre-processed image URL
                geminiOptions, // Options used by API to construct the actual generation prompt
                (prog, _, message) => { // Use the onProgress callback
                  // Adjust progress based on whether removal happened (starts from 40% or 10%)
                  const progressStart = needsFurnitureRemoval ? 40 : 10;
                  setProgress(progressStart + prog * (100 - progressStart)); 
                  // Optionally display the message somewhere
                  console.log(`Gemini Progress: ${prog * 100}% - ${message}`);
                }
              );

              generatedUrl = geminiResultUrl; // Store the URL for potential later use/display
              setGeneratedImageUrl(generatedUrl); // Set the final Supabase URL for display

              // --- Create and Upload Thumbnail ---
              const thumbnailUrl = await createAndUploadThumbnail(generatedUrl);
              // --- End Thumbnail ---

              // Prepare metadata for saving (use the constructed simplified prompt)
              generationMetadata = {
                url: generatedUrl,
                prompt: actualGeminiPrompt, // Use the prompt returned by the API
                thumbnail_url: thumbnailUrl, // <<< ADD THUMBNAIL URL HERE
                user_id: user.id,
                original_image_url: uploadedImageUrl,
                negative_prompt: negativePrompt, // Include negative prompt if applicable
                style: geminiOptions.style, // Use style from Gemini options
                mode: selectedWorkflow.mode, // Use mode from workflow definition
                checkpoint: 'gemini-2.0-flash-exp-image-generation', // Log the correct image model
                enhanced_prompt: autoEnhance ? finalPrompt : null, // Use the potentially enhanced prompt
                workflow_mode: selectedWorkflow.id,
                workflow_title: selectedWorkflow.title,
                workflow_version: selectedWorkflow.version,
                // Only add these fields if not furniture removal workflow
                ...(selectedWorkflow.id !== 'furniture-removal' && {
                  furniture_color: geminiOptions.furnitureColor, // Add furniture color to metadata
                  is_room_furnished: geminiOptions.isRoomFurnished, // Add furnished flag to metadata
                  room_type: geminiOptions.roomType // Add room type to metadata
                }),
                // Add propertyInfo logic here if needed (will be added later)
              };
            }
            } else {
              // --- ComfyDeploy Generation (Existing Logic) ---
              console.log("Initiating ComfyDeploy workflow...");
              let workflowDeploymentId = uploadedMaskUrl
                ? '716c3ff0-ada2-44c4-b4bd-7e576873ac1b'  // Masked Sketch To Furnish
                : selectedWorkflow?.deploymentId || deploymentId;

            // Get the style image URL directly from the state
            const styleImageUrl = inputs.input_styleimage;
            console.log('Current style image URL from state before API call:', styleImageUrl);

            const apiInputs = selectedWorkflow?.simpleInputs ? {
              input_image: uploadedImageUrl,
              positive: prompt,
              input_negative: negativePrompt || '',
              input_batch: 1
            } : {
              input_image: imageUrlForGeneration, // Use the potentially pre-processed image URL
              // Use the specifically translated prompt for virtual-staging or staging, otherwise use the default logic
              positive: (selectedWorkflow?.id === 'virtual-staging' || selectedWorkflow?.id === 'staging') && translatedApiPrompt
                        ? translatedApiPrompt
                        : (inputs.roomPromptEn || finalPrompt), // Fallback logic uses enhanced/original prompt
              input_checkpoint: inputCheckpoint,
              ...(selectedWorkflow?.id === 'inpaint' ? {
                room_type: inputs.room_type || 'Living Room',
                style: inputs.style || 'contemporary',
                color_theme: inputs.color_theme || 'original',
                // Conditionally add furniture_color only if it's not null
                ...(inputs.furniture_color && { furniture_color: inputs.furniture_color }),
                is_room_furnished: isRoomFurnished
              } : {}),
              ...(selectedWorkflow?.id === 'staging' || selectedWorkflow?.id === 'virtual-staging' ? {
                room_type: inputs.room_type || 'Living Room',
                style: inputs.style || 'contemporary',
                color_theme: inputs.color_theme || 'original',
                input_room: inputs.room_type === 'Bedroom' ? '/private_models/unet/bedroom.pth' :
                           inputs.room_type === 'Bathroom' ? '/private_models/unet/bathroom.pth' :
                           inputs.room_type === 'Kitchen' ? '/private_models/unet/kitchen.pth' :
                           inputs.room_type === 'Dining Room' ? '/private_models/unet/kitchen.pth' :
                           '/private_models/unet/livingroom.pth'
              } : {}),
              ...(selectedWorkflow?.id === 'staging' || selectedWorkflow?.id === 'virtual-staging' ? {
                room_type: inputs.room_type || 'Living Room',
                style: inputs.style || 'contemporary',
                color_theme: inputs.color_theme || 'original',
                // Conditionally add furniture_color only if it's not null
                ...(inputs.furniture_color && { furniture_color: inputs.furniture_color }),
                is_room_furnished: isRoomFurnished,
                input_room: inputs.room_type === 'Bedroom' ? '/private_models/unet/bedroom.pth' :
                           inputs.room_type === 'Bathroom' ? '/private_models/unet/bathroom.pth' :
                           inputs.room_type === 'Kitchen' ? '/private_models/unet/kitchen.pth' :
                           inputs.room_type === 'Dining Room' ? '/private_models/unet/kitchen.pth' :
                           '/private_models/unet/livingroom.pth',
                // Nested redundant section removed for clarity and correctness
              } : {}),
              // Include style image if required by the workflow or if it's the 'staging' workflow
              ...((selectedWorkflow?.requiresStyleImage || selectedWorkflow?.id === 'staging') && styleImageUrl ? {
                // Use the style image URL from state with a fresh timestamp
                input_styleimage: `${styleImageUrl.split('?')[0]}?t=${Date.now()}`,
                // Add additional timestamp parameter to force the API to use the latest image
                _timestamp: Date.now(),
                _cache_buster: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2),
                // Add debug info to track which URL is being used
                _debug_styleimage_time: new Date().toISOString(),
                _debug_styleimage_url: styleImageUrl // Use the state variable for debugging
              } : {}),
              // Moved workflow_id and version to the correct level
              workflow_id: workflowDeploymentId,
              version: "1.0"
            };

              if (uploadedMaskUrl) {
                apiInputs.input_mask = uploadedMaskUrl;
              }

              console.log('Final ComfyDeploy API inputs:', JSON.stringify(apiInputs, null, 2));

              if (!workflowDeploymentId) {
                throw new Error('No valid deployment ID found for the selected ComfyDeploy workflow');
              }

              const outputs = await runComfyDeployWorkflow(
                workflowDeploymentId,
                apiInputs,
                (prog, _, message) => { // Use the onProgress callback
                  // Adjust progress based on whether removal happened (starts from 40% or 10%)
                  const progressStart = needsFurnitureRemoval ? 40 : 10;
                  setProgress(progressStart + prog * (100 - progressStart));
                  console.log(`ComfyDeploy Progress: ${progressStart + prog * (100 - progressStart)}% - ${message || ''}`);
                }
              );

              // Process ComfyDeploy outputs
              if (outputs && outputs.length > 0) {
                 // Find the first valid image URL from the outputs
                 let outputUrl = null;
                 for (const outputItem of outputs) {
                   if (outputItem?.data?.images?.[0]?.url) {
                     outputUrl = outputItem.data.images[0].url;
                     break;
                   } else if (outputItem?.url) { // Fallback for simpler output structures
                     outputUrl = outputItem.url;
                     break;
                   }
                 }

                 if (outputUrl) {
                   setGeneratedImageUrl(outputUrl); // Display the direct ComfyDeploy URL first
                   console.log("ComfyDeploy output URL:", outputUrl);

                   // Asynchronously upload to Supabase and update the state
                   (async () => {
                     try {
                       const response = await fetch(outputUrl);
                       const blob = await response.blob();
                       const fileName = `generated-${Date.now()}.png`; // Assume png for now
                       const { data: uploadData, error: uploadError } = await supabase.storage
                         .from('generated-images')
                         .upload(fileName, blob);

                       if (uploadError) throw uploadError;

                       const publicUrl = getPublicUrl('generated-images', fileName);
                       setGeneratedImageUrl(publicUrl); // Update state with Supabase URL
                       console.log("Uploaded ComfyDeploy output to Supabase:", publicUrl);

                       // Prepare metadata for saving (use the final Supabase URL)
                       generationMetadata = {
                         url: publicUrl,
                         prompt: generationMetadata.prompt, // Use the prompt from the metadata object
                         user_id: user.id,
                         original_image_url: uploadedImageUrl,
                         negative_prompt: negativePrompt,
                         style: selectedStyle,
                         mode: mode,
                         checkpoint: inputCheckpoint,
                         enhanced_prompt: autoEnhance ? finalPrompt : null,
                         workflow_mode: selectedWorkflow.id,
                         workflow_title: selectedWorkflow.title,
                         workflow_version: selectedWorkflow.version,
                         // Only add these fields if not furniture removal workflow
                         ...(selectedWorkflow.id !== 'furniture-removal' && {
                           // Conditionally add furniture_color metadata only if it exists
                           ...(inputs.furniture_color && { furniture_color: inputs.furniture_color }),
                           is_room_furnished: isRoomFurnished,
                           // Add room_type only if not furniture removal
                           ...(inputs.room_type && { room_type: inputs.room_type })
                         }),
                         // Add removal_intensity only for furniture removal
                         ...(selectedWorkflow.id === 'furniture-removal' && {
                           removal_intensity: inputs.removal_intensity || 'complete'
                         }),
                         ...(selectedWorkflow?.id === 'staging' || selectedWorkflow?.id === 'virtual-staging' ? {
                           input_room: inputs.room_type === 'Bedroom' ? '/private_models/unet/bedroom.pth' :
                                      inputs.room_type === 'Bathroom' ? '/private_models/unet/bathroom.pth' :
                                      inputs.room_type === 'Kitchen' ? '/private_models/unet/kitchen.pth' :
                                      inputs.room_type === 'Dining Room' ? '/private_models/unet/kitchen.pth' :
                                      '/private_models/unet/livingroom.pth'
                         } : {}),
                         // Add propertyInfo logic here if needed
                       };

                       // Save metadata using the Supabase URL
                       // Pass the prompt that was sent to the ComfyDeploy API
                       await saveMetadataToDatabase(generationMetadata, apiInputs.positive);

                     } catch (supabaseError) {
                       console.error("Error uploading ComfyDeploy output to Supabase:", supabaseError);
                       // Keep the direct ComfyDeploy URL if Supabase upload fails
                       // Prepare metadata using the direct URL if upload failed
                       const fallbackMetadata = {
                         url: outputUrl, // Use the direct ComfyDeploy URL
                         prompt: apiInputs.positive, // Use the prompt sent to the API
                         user_id: user.id,
                         original_image_url: uploadedImageUrl,
                         negative_prompt: negativePrompt,
                         style: selectedStyle,
                         mode: mode,
                         checkpoint: inputCheckpoint,
                         enhanced_prompt: autoEnhance ? finalPrompt : null,
                         workflow_mode: selectedWorkflow.id,
                         workflow_title: selectedWorkflow.title,
                         workflow_version: selectedWorkflow.version,
                         // Only add these fields if not furniture removal workflow
                         ...(selectedWorkflow.id !== 'furniture-removal' && {
                           // Conditionally add furniture_color metadata only if it exists
                           ...(inputs.furniture_color && { furniture_color: inputs.furniture_color }),
                           is_room_furnished: isRoomFurnished,
                           // Add room_type only if not furniture removal
                           ...(inputs.room_type && { room_type: inputs.room_type })
                         }),
                         // Add removal_intensity only for furniture removal
                         ...(selectedWorkflow.id === 'furniture-removal' && {
                           removal_intensity: inputs.removal_intensity || 'complete'
                         }),
                         // Add propertyInfo logic here if needed
                       };

                       // --- Create and Upload Thumbnail (using direct ComfyDeploy URL as fallback) ---
                       const fallbackThumbnailUrl = await createAndUploadThumbnail(outputUrl);
                       // --- End Thumbnail ---

                       // Save metadata using the fallback ComfyDeploy URL and fallback thumbnail URL
                       // Pass the prompt that was sent to the ComfyDeploy API
                       await saveMetadataToDatabase(fallbackMetadata, apiInputs.positive, fallbackThumbnailUrl);
                     }
                   })(); // End of async Supabase upload block
                 } else {
                   console.warn("No valid image URL found in ComfyDeploy outputs.");
                   // Even if no image URL, maybe save a record indicating failure? Optional.
                 }
              } else {
                console.warn("No outputs received from ComfyDeploy workflow.");
                
                // Refund credits since no outputs were received
                try {
                  // Get the current user from Supabase auth
                  const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
                  
                  if (userError || !currentUser || !currentUser.id) {
                    console.error('Cannot refund credits: User authentication failed', userError);
                    throw new Error('User authentication required for credit refund');
                  }
                  
                  // Calculate credits to refund
                  const requiredCredits = parseInt(selectedWorkflow.credits || '10', 10);
                  
                  // Create an RPC call object that will be handled specially in updateProfile
                  const rpcCall = {
                    rpc: true,
                    result: supabase.rpc('increment_credits', { amount: requiredCredits })
                  };
              
                  // Use updateProfile from supabaseClient to add credits back
                  const updatedProfile = await updateProfile(currentUser.id, {
                    credits_remaining: rpcCall,
                    updated_at: new Date()
                  });
                  
                  if (!updatedProfile) {
                    throw new Error('Failed to update credits');
                  }
                  
                  console.log(`Refunded ${requiredCredits} credits due to no outputs received`);
                  
                  // Show alert to user
                  alert('Görsel oluşturulamadı. Kredileriniz iade edildi. Lütfen tekrar deneyin.');
                } catch (refundError) {
                  console.error('Error refunding credits:', refundError);
                }
              }
           } // End of ComfyDeploy vs Gemini logic


           // --- Save Metadata (Moved inside async blocks) ---
           // Call the helper function (defined outside the handler now)
           if (isGeminiWorkflow && generationMetadata.url) { // Ensure URL exists before saving
             // Pass the actual prompt returned by the Gemini API and the thumbnail URL
             await saveMetadataToDatabase(generationMetadata, generationMetadata.prompt, generationMetadata.thumbnail_url); // Pass thumbnail URL if added to metadata earlier
           }
           // For ComfyDeploy, saving is handled within its async upload block above


           // --- Scroll to results after generation attempt ---
            const element = document.querySelector('.generated-images-section'); // Adjust selector if needed
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

          } catch (error) {
            console.error('Generation process error:', error);
            
            // Refund credits on error
            try {
              // Get the current user from Supabase auth
              const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
              
              if (userError || !currentUser || !currentUser.id) {
                console.error('Cannot refund credits: User authentication failed', userError);
                throw new Error('User authentication required for credit refund');
              }
              
              // Calculate credits to refund
              const requiredCredits = parseInt(selectedWorkflow.credits || '10', 10);
              
              // Create an RPC call object that will be handled specially in updateProfile
              const rpcCall = {
                rpc: true,
                result: supabase.rpc('increment_credits', { amount: requiredCredits })
              };
              
              // Use updateProfile from supabaseClient to add credits back
              const updatedProfile = await updateProfile(currentUser.id, {
                credits_remaining: rpcCall,
                updated_at: new Date()
              });
              
              if (!updatedProfile) {
                throw new Error('Failed to update credits');
              }
              
              console.log(`Refunded ${requiredCredits} credits due to error: ${error.message}`);
              
              // Display a user-friendly error message with refund information
              alert(`Görsel oluşturulamadı: ${error.message || 'Bilinmeyen bir hata oluştu'}. Kredileriniz iade edildi.`);
            } catch (refundError) {
              console.error('Error refunding credits:', refundError);
              // Display original error message if refund fails
              alert(`Generation failed: ${error.message || 'An unknown error occurred. Please check console for details.'}`);
            }
          } finally {
            setLoading(false); // Ensure loading is always set to false at the end
          }
        }}
      >
            {/* Animation Overlay using framer-motion */}
            <motion.div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                background: `linear-gradient(120deg, 
                  rgba(0, 53, 128, 0.6) 0%, 
                  rgba(0, 74, 173, 0.7) 40%, 
                  rgba(0, 90, 200, 0.8) 60%, 
                  rgba(0, 53, 128, 0.6) 100%
                )`,
                backgroundSize: '100% 100%', // Static background size
                borderRadius: 'inherit', // Inherit button's border radius
                zIndex: 0, // Ensure it's behind the text
                pointerEvents: 'none', // Prevent interaction
              }}
              initial={{ width: '0%', opacity: 0 }} // Start with 0 width and hidden
              animate={loading ? { // Animate only when loading
                width: `${progress}%`, // Animate width based on progress state
                opacity: 1, // Fade in
                transition: {
                  width: { duration: 0.8, ease: "easeInOut" }, // Slower, smoother width transition
                  opacity: { duration: 0.5 } // Slower fade in
                }
              } : { // Animate out when not loading
                width: '0%', // Reset width
                opacity: 0,
                transition: { duration: 0.6, delay: 0.2 } // Slower fade out with longer delay
              }}
            />
            {/* Button Text (ensure it's above the overlay) */}
            <span style={{ position: 'relative', zIndex: 1 }}>
              {loading ? 'İşleniyor...' : selectedWorkflow?.id === 'furniture-removal' ? 'Mobilyaları Temizle' : 'Oluştur'}
            </span>
            </GenerateButton>
          </Box>
        </Box>
      </Box>

        <ErrorBoundary fallback={<div>Error loading comparison view</div>}>
          <Suspense fallback={
            <Box sx={modalStyle}>
              <CircularProgress />
            </Box>
          }>
            <Modal
              open={showCompare}
              onClose={() => memoizedSetShowCompare(false)}
              aria-labelledby="compare-modal"
            >
              <Box>
                {memoizedModalContent}
              </Box>
            </Modal>
          </Suspense>
        </ErrorBoundary>
      </StyledBox>
    ), [
      showCompare, 
      uploadedImageUrl, 
      generatedImageUrl,
      mode,
      selectedWorkflow,
      selectedStyle,
      prompt,
      autoEnhance,
      loading,
      progress,
      uploadedImage,
      uploadedMaskUrl,
      handleFileInput,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      isDragging,
      isMaskingEnabled,
      memoizedModalContent,
      theme.palette.mode,
      inputs, // Add inputs state to dependency array
      isRoomFurnished, // Add isRoomFurnished state to dependency array
      updateDynamicPrompt, // Add updateDynamicPrompt function to dependency array
      roomPromptTemplates // Add roomPromptTemplates to dependency array
    ]);

    return (
      <Box sx={{ 
        position: 'relative', 
        display: 'flex',
        gap: 3,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100%',
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        padding: { xs: 2, sm: 3, md: 4 } // Reverted paddingTop change
        // paddingTop: { xs: 18, sm: 20 } // Increased padding top significantly to clear indicator
      }}>
        {/* Workflow Title Indicator */}
        <WorkflowTitleIndicator>
          <Link to="/workflows" style={{ textDecoration: 'none' }}>
            <BackButton size="small">
              <ArrowBackIcon fontSize="small" />
            </BackButton>
          </Link>
          <Typography variant="subtitle2" sx={{
            fontWeight: 600,
            color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
            whiteSpace: 'nowrap'
          }}>
            {selectedWorkflow?.title || 'İş Akışı'}
          </Typography>
          {selectedWorkflow?.id === 'fast-staging' && (
            <FastStagingChip>
              <span role="img" aria-label="lightning" style={{ marginRight: '4px' }}>⚡</span> Hızlı
            </FastStagingChip>
          )}
          {selectedWorkflow?.id === 'virtual-staging' && (
            <QualityChip>
              <span role="img" aria-label="diamond" style={{ marginRight: '4px' }}>💎</span> Kalite
            </QualityChip>
          )}
          {selectedWorkflow?.id === 'furniture-removal' && (
            <FastStagingChip sx={{
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(233, 30, 99, 0.2), rgba(156, 39, 176, 0.2))'
                : 'linear-gradient(135deg, rgba(233, 30, 99, 0.15), rgba(156, 39, 176, 0.15))',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(233, 30, 99, 0.4)' : 'rgba(156, 39, 176, 0.4)'}`,
              color: theme.palette.mode === 'dark' ? '#f48fb1' : '#9c27b0',
            }}>
              <span role="img" aria-label="broom" style={{ marginRight: '4px' }}>🧹</span> Hızlı
            </FastStagingChip>
          )}
        </WorkflowTitleIndicator>

        <MaskSaveSuccess show={showMaskSuccess} />
        <CreditAlert 
          open={showCreditAlert}
          onClose={() => {
            setShowCreditAlert(false);
            navigate('/subscription', { 
              state: { 
                message: 'Kredi limitiniz yetersiz. Lütfen aboneliğinizi yükseltin veya kredi satın alın.'
              }
            });
          }}
          requiredCredits={creditAlertData.required}
          availableCredits={creditAlertData.available}
        />
        <VirtualizedContainer>
          {renderForm}
          <GalleryThumbnails 
            onImageSelect={handleGalleryImageSelect} 
            newImage={newGalleryImage} // Pass the new image data
          />
        </VirtualizedContainer>
      </Box>
    );
});

const CanvasEditor = ({ onStageChange }) => {
  return (
    <AuthProvider>
      <CanvasEditorContent onStageChange={onStageChange} />
    </AuthProvider>
  );
};

export default CanvasEditor;
